// NOTE: This file is copied from the google-apps-script repository, in which it is generated automatically using the supabase CLI

import * as SosTypes from "./sos-types";

/**
 * JSON type, limited to a recursive depth of 5 for improved performance
 * See: https://github.com/supabase/supabase-js/issues/808#issuecomment-2143815626
 */
export type Json<D extends number = 5, DA extends any[] = []> =
  | string
  | number
  | boolean
  | null
  | (D extends DA["length"] ? any : { [key: string]: Json<D, [0, ...DA]> | undefined })
  | (D extends DA["length"] ? any : Json<D, [0, ...DA]>[]);

export type Database = {
  graphql_public: {
    Tables: {
      [_ in never]: never;
    };
    Views: {
      [_ in never]: never;
    };
    Functions: {
      graphql: {
        Args: {
          query?: string;
          variables?: Json;
          extensions?: Json;
          operationName?: string;
        };
        Returns: Json;
      };
    };
    Enums: {
      [_ in never]: never;
    };
    CompositeTypes: {
      [_ in never]: never;
    };
  };
  public: {
    Tables: {
      Account: {
        Row: {
          accountNumber: string | null;
          accountType: string;
          deleted: boolean;
          description: string | null;
          fullname: string;
          id: number;
          name: string;
          parentId: number | null;
          sublevel: number;
          syncTimestamp: string;
          syncToken: number;
        };
        Insert: {
          accountNumber?: string | null;
          accountType: string;
          deleted: boolean;
          description?: string | null;
          fullname: string;
          id: number;
          name: string;
          parentId?: number | null;
          sublevel: number;
          syncTimestamp: string;
          syncToken: number;
        };
        Update: {
          accountNumber?: string | null;
          accountType?: string;
          deleted?: boolean;
          description?: string | null;
          fullname?: string;
          id?: number;
          name?: string;
          parentId?: number | null;
          sublevel?: number;
          syncTimestamp?: string;
          syncToken?: number;
        };
        Relationships: [
          {
            foreignKeyName: "Account_parentId_fkey";
            columns: ["parentId"];
            isOneToOne: false;
            referencedRelation: "Account";
            referencedColumns: ["id"];
          },
        ];
      };
      Adjustment: {
        Row: {
          accountId: number | null;
          archived: boolean;
          comment: string | null;
          date: string;
          deleted: boolean;
          departmentId: number | null;
          id: number;
          locationId: number;
          number: string;
          starred: number;
          syncTimestamp: string;
          syncToken: number;
          total: number;
        };
        Insert: {
          accountId?: number | null;
          archived: boolean;
          comment?: string | null;
          date: string;
          deleted: boolean;
          departmentId?: number | null;
          id: number;
          locationId: number;
          number: string;
          starred: number;
          syncTimestamp: string;
          syncToken: number;
          total: number;
        };
        Update: {
          accountId?: number | null;
          archived?: boolean;
          comment?: string | null;
          date?: string;
          deleted?: boolean;
          departmentId?: number | null;
          id?: number;
          locationId?: number;
          number?: string;
          starred?: number;
          syncTimestamp?: string;
          syncToken?: number;
          total?: number;
        };
        Relationships: [
          {
            foreignKeyName: "Adjustment_accountId_fkey";
            columns: ["accountId"];
            isOneToOne: false;
            referencedRelation: "Account";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "Adjustment_departmentId_fkey";
            columns: ["departmentId"];
            isOneToOne: false;
            referencedRelation: "Department";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "Adjustment_locationId_fkey";
            columns: ["locationId"];
            isOneToOne: false;
            referencedRelation: "Location";
            referencedColumns: ["id"];
          },
        ];
      };
      AdjustmentLineItem: {
        Row: {
          adjustmentId: number;
          binId: number | null;
          classId: number | null;
          deleted: boolean;
          description: string | null;
          id: number;
          itemId: number;
          jobId: number | null;
          linenumber: number;
          lotId: number | null;
          newQuantity: number;
          quantityDiff: number;
          serials: SosTypes.Reference[] | null;
          syncTimestamp: string;
          uomId: number | null;
          valueDiff: number;
          workcenterId: number | null;
        };
        Insert: {
          adjustmentId: number;
          binId?: number | null;
          classId?: number | null;
          deleted: boolean;
          description?: string | null;
          id: number;
          itemId: number;
          jobId?: number | null;
          linenumber: number;
          lotId?: number | null;
          newQuantity: number;
          quantityDiff: number;
          serials?: SosTypes.Reference[] | null;
          syncTimestamp: string;
          uomId?: number | null;
          valueDiff: number;
          workcenterId?: number | null;
        };
        Update: {
          adjustmentId?: number;
          binId?: number | null;
          classId?: number | null;
          deleted?: boolean;
          description?: string | null;
          id?: number;
          itemId?: number;
          jobId?: number | null;
          linenumber?: number;
          lotId?: number | null;
          newQuantity?: number;
          quantityDiff?: number;
          serials?: SosTypes.Reference[] | null;
          syncTimestamp?: string;
          uomId?: number | null;
          valueDiff?: number;
          workcenterId?: number | null;
        };
        Relationships: [
          {
            foreignKeyName: "AdjustmentLineItem_adjustmentId_fkey";
            columns: ["adjustmentId"];
            isOneToOne: false;
            referencedRelation: "Adjustment";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "AdjustmentLineItem_binId_fkey";
            columns: ["binId"];
            isOneToOne: false;
            referencedRelation: "Bin";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "AdjustmentLineItem_classId_fkey";
            columns: ["classId"];
            isOneToOne: false;
            referencedRelation: "Class";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "AdjustmentLineItem_itemId_fkey";
            columns: ["itemId"];
            isOneToOne: false;
            referencedRelation: "Item";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "AdjustmentLineItem_itemId_uomId_fkey";
            columns: ["itemId", "uomId"];
            isOneToOne: false;
            referencedRelation: "ItemUOM";
            referencedColumns: ["itemId", "uomId"];
          },
          {
            foreignKeyName: "AdjustmentLineItem_jobId_fkey";
            columns: ["jobId"];
            isOneToOne: false;
            referencedRelation: "Job";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "AdjustmentLineItem_uomId_fkey";
            columns: ["uomId"];
            isOneToOne: false;
            referencedRelation: "UOM";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "AdjustmentLineItem_workcenterId_fkey";
            columns: ["workcenterId"];
            isOneToOne: false;
            referencedRelation: "Workcenter";
            referencedColumns: ["id"];
          },
        ];
      };
      AssemblyBomLineItem: {
        Row: {
          assemblyId: number;
          componentId: number;
          lineNumber: number;
          notes: string | null;
          quantity: number;
        };
        Insert: {
          assemblyId: number;
          componentId: number;
          lineNumber: number;
          notes?: string | null;
          quantity: number;
        };
        Update: {
          assemblyId?: number;
          componentId?: number;
          lineNumber?: number;
          notes?: string | null;
          quantity?: number;
        };
        Relationships: [
          {
            foreignKeyName: "AssemblyBomLineItem_assemblyId_fkey";
            columns: ["assemblyId"];
            isOneToOne: false;
            referencedRelation: "Item";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "AssemblyBomLineItem_componentId_fkey";
            columns: ["componentId"];
            isOneToOne: false;
            referencedRelation: "Item";
            referencedColumns: ["id"];
          },
        ];
      };
      Bin: {
        Row: {
          deleted: boolean;
          description: string | null;
          id: number;
          locationId: number;
          number: string;
          sortOrder: number;
          syncTimestamp: string;
          syncToken: number;
        };
        Insert: {
          deleted: boolean;
          description?: string | null;
          id: number;
          locationId: number;
          number: string;
          sortOrder: number;
          syncTimestamp: string;
          syncToken: number;
        };
        Update: {
          deleted?: boolean;
          description?: string | null;
          id?: number;
          locationId?: number;
          number?: string;
          sortOrder?: number;
          syncTimestamp?: string;
          syncToken?: number;
        };
        Relationships: [
          {
            foreignKeyName: "Bin_locationId_fkey";
            columns: ["locationId"];
            isOneToOne: false;
            referencedRelation: "Location";
            referencedColumns: ["id"];
          },
        ];
      };
      BinItem: {
        Row: {
          binId: number;
          deleted: boolean;
          itemId: number;
          quantity: number;
          syncTimestamp: string;
        };
        Insert: {
          binId: number;
          deleted: boolean;
          itemId: number;
          quantity: number;
          syncTimestamp: string;
        };
        Update: {
          binId?: number;
          deleted?: boolean;
          itemId?: number;
          quantity?: number;
          syncTimestamp?: string;
        };
        Relationships: [
          {
            foreignKeyName: "BinItem_binId_fkey";
            columns: ["binId"];
            isOneToOne: false;
            referencedRelation: "Bin";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "BinItem_itemId_fkey";
            columns: ["itemId"];
            isOneToOne: false;
            referencedRelation: "Item";
            referencedColumns: ["id"];
          },
        ];
      };
      BomPathPurchasingPriority: {
        Row: {
          id: number;
          jobId: number | null;
          path: string;
          priority: number;
          version: number;
        };
        Insert: {
          id?: number;
          jobId?: number | null;
          path: string;
          priority: number;
          version?: number;
        };
        Update: {
          id?: number;
          jobId?: number | null;
          path?: string;
          priority?: number;
          version?: number;
        };
        Relationships: [
          {
            foreignKeyName: "BomPathPurchasingPriority_jobId_fkey";
            columns: ["jobId"];
            isOneToOne: false;
            referencedRelation: "Job";
            referencedColumns: ["id"];
          },
        ];
      };
      Build: {
        Row: {
          archived: boolean;
          comment: string | null;
          date: string;
          deleted: boolean;
          departmentId: number | null;
          id: number;
          jobId: number | null;
          locationId: number;
          number: string;
          starred: number;
          syncTimestamp: string;
          syncToken: number;
          total: number;
          workcenterId: number | null;
        };
        Insert: {
          archived: boolean;
          comment?: string | null;
          date: string;
          deleted: boolean;
          departmentId?: number | null;
          id: number;
          jobId?: number | null;
          locationId: number;
          number: string;
          starred: number;
          syncTimestamp: string;
          syncToken: number;
          total: number;
          workcenterId?: number | null;
        };
        Update: {
          archived?: boolean;
          comment?: string | null;
          date?: string;
          deleted?: boolean;
          departmentId?: number | null;
          id?: number;
          jobId?: number | null;
          locationId?: number;
          number?: string;
          starred?: number;
          syncTimestamp?: string;
          syncToken?: number;
          total?: number;
          workcenterId?: number | null;
        };
        Relationships: [
          {
            foreignKeyName: "Build_departmentId_fkey";
            columns: ["departmentId"];
            isOneToOne: false;
            referencedRelation: "Department";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "Build_jobId_fkey";
            columns: ["jobId"];
            isOneToOne: false;
            referencedRelation: "Job";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "Build_locationId_fkey";
            columns: ["locationId"];
            isOneToOne: false;
            referencedRelation: "Location";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "Build_workcenterId_fkey";
            columns: ["workcenterId"];
            isOneToOne: false;
            referencedRelation: "Workcenter";
            referencedColumns: ["id"];
          },
        ];
      };
      BuildCustomFieldValue: {
        Row: {
          buildId: number;
          customFieldDefinitionId: number;
          deleted: boolean;
          syncTimestamp: string;
          value: string | null;
        };
        Insert: {
          buildId: number;
          customFieldDefinitionId: number;
          deleted: boolean;
          syncTimestamp: string;
          value?: string | null;
        };
        Update: {
          buildId?: number;
          customFieldDefinitionId?: number;
          deleted?: boolean;
          syncTimestamp?: string;
          value?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "BuildCustomFieldValue_buildId_fkey";
            columns: ["buildId"];
            isOneToOne: false;
            referencedRelation: "Build";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "BuildCustomFieldValue_customFieldDefinitionId_fkey";
            columns: ["customFieldDefinitionId"];
            isOneToOne: false;
            referencedRelation: "CustomFieldDefinition";
            referencedColumns: ["id"];
          },
        ];
      };
      BuildInputLineItem: {
        Row: {
          binId: number | null;
          buildId: number;
          classId: number | null;
          deleted: boolean;
          description: string | null;
          id: number;
          itemId: number;
          jobId: number | null;
          lineNumber: number;
          lotExpiration: string | null;
          lotId: number | null;
          notes: string | null;
          quantity: number;
          serials: SosTypes.Reference[] | null;
          syncTimestamp: string;
          uomId: number | null;
          volume: number;
          volumeunit: string | null;
          waste: boolean;
          weight: number;
          weightunit: string | null;
          workcenterId: number | null;
          workerId: number | null;
        };
        Insert: {
          binId?: number | null;
          buildId: number;
          classId?: number | null;
          deleted: boolean;
          description?: string | null;
          id: number;
          itemId: number;
          jobId?: number | null;
          lineNumber: number;
          lotExpiration?: string | null;
          lotId?: number | null;
          notes?: string | null;
          quantity: number;
          serials?: SosTypes.Reference[] | null;
          syncTimestamp: string;
          uomId?: number | null;
          volume: number;
          volumeunit?: string | null;
          waste: boolean;
          weight: number;
          weightunit?: string | null;
          workcenterId?: number | null;
          workerId?: number | null;
        };
        Update: {
          binId?: number | null;
          buildId?: number;
          classId?: number | null;
          deleted?: boolean;
          description?: string | null;
          id?: number;
          itemId?: number;
          jobId?: number | null;
          lineNumber?: number;
          lotExpiration?: string | null;
          lotId?: number | null;
          notes?: string | null;
          quantity?: number;
          serials?: SosTypes.Reference[] | null;
          syncTimestamp?: string;
          uomId?: number | null;
          volume?: number;
          volumeunit?: string | null;
          waste?: boolean;
          weight?: number;
          weightunit?: string | null;
          workcenterId?: number | null;
          workerId?: number | null;
        };
        Relationships: [
          {
            foreignKeyName: "BuildInputLineItem_binId_fkey";
            columns: ["binId"];
            isOneToOne: false;
            referencedRelation: "Bin";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "BuildInputLineItem_buildId_fkey";
            columns: ["buildId"];
            isOneToOne: false;
            referencedRelation: "Build";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "BuildInputLineItem_classId_fkey";
            columns: ["classId"];
            isOneToOne: false;
            referencedRelation: "Class";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "BuildInputLineItem_itemId_fkey";
            columns: ["itemId"];
            isOneToOne: false;
            referencedRelation: "Item";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "BuildInputLineItem_itemId_uomId_fkey";
            columns: ["itemId", "uomId"];
            isOneToOne: false;
            referencedRelation: "ItemUOM";
            referencedColumns: ["itemId", "uomId"];
          },
          {
            foreignKeyName: "BuildInputLineItem_jobId_fkey";
            columns: ["jobId"];
            isOneToOne: false;
            referencedRelation: "Job";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "BuildInputLineItem_uomId_fkey";
            columns: ["uomId"];
            isOneToOne: false;
            referencedRelation: "UOM";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "BuildInputLineItem_workcenterId_fkey";
            columns: ["workcenterId"];
            isOneToOne: false;
            referencedRelation: "Workcenter";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "BuildInputLineItem_workerId_fkey";
            columns: ["workerId"];
            isOneToOne: false;
            referencedRelation: "Worker";
            referencedColumns: ["id"];
          },
        ];
      };
      BuildOutputLineItem: {
        Row: {
          binId: number | null;
          buildId: number;
          classId: number | null;
          deleted: boolean;
          description: string | null;
          id: number;
          itemId: number;
          jobId: number | null;
          lineNumber: number;
          linkedWorkOrderLineItemId: number | null;
          lotExpiration: string | null;
          lotId: number | null;
          quantity: number;
          serials: SosTypes.Reference[] | null;
          syncTimestamp: string;
          uomId: number | null;
          volume: number;
          volumeunit: string | null;
          waste: boolean;
          weight: number;
          weightunit: string | null;
          workcenterId: number | null;
          workerId: number | null;
        };
        Insert: {
          binId?: number | null;
          buildId: number;
          classId?: number | null;
          deleted: boolean;
          description?: string | null;
          id: number;
          itemId: number;
          jobId?: number | null;
          lineNumber: number;
          linkedWorkOrderLineItemId?: number | null;
          lotExpiration?: string | null;
          lotId?: number | null;
          quantity: number;
          serials?: SosTypes.Reference[] | null;
          syncTimestamp: string;
          uomId?: number | null;
          volume: number;
          volumeunit?: string | null;
          waste: boolean;
          weight: number;
          weightunit?: string | null;
          workcenterId?: number | null;
          workerId?: number | null;
        };
        Update: {
          binId?: number | null;
          buildId?: number;
          classId?: number | null;
          deleted?: boolean;
          description?: string | null;
          id?: number;
          itemId?: number;
          jobId?: number | null;
          lineNumber?: number;
          linkedWorkOrderLineItemId?: number | null;
          lotExpiration?: string | null;
          lotId?: number | null;
          quantity?: number;
          serials?: SosTypes.Reference[] | null;
          syncTimestamp?: string;
          uomId?: number | null;
          volume?: number;
          volumeunit?: string | null;
          waste?: boolean;
          weight?: number;
          weightunit?: string | null;
          workcenterId?: number | null;
          workerId?: number | null;
        };
        Relationships: [
          {
            foreignKeyName: "BuildOutputLineItem_binId_fkey";
            columns: ["binId"];
            isOneToOne: false;
            referencedRelation: "Bin";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "BuildOutputLineItem_buildId_fkey";
            columns: ["buildId"];
            isOneToOne: false;
            referencedRelation: "Build";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "BuildOutputLineItem_classId_fkey";
            columns: ["classId"];
            isOneToOne: false;
            referencedRelation: "Class";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "BuildOutputLineItem_itemId_fkey";
            columns: ["itemId"];
            isOneToOne: false;
            referencedRelation: "Item";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "BuildOutputLineItem_itemId_uomId_fkey";
            columns: ["itemId", "uomId"];
            isOneToOne: false;
            referencedRelation: "ItemUOM";
            referencedColumns: ["itemId", "uomId"];
          },
          {
            foreignKeyName: "BuildOutputLineItem_jobId_fkey";
            columns: ["jobId"];
            isOneToOne: false;
            referencedRelation: "Job";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "BuildOutputLineItem_linkedWorkOrderLineItemId_fkey";
            columns: ["linkedWorkOrderLineItemId"];
            isOneToOne: false;
            referencedRelation: "WorkOrderLineItem";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "BuildOutputLineItem_uomId_fkey";
            columns: ["uomId"];
            isOneToOne: false;
            referencedRelation: "UOM";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "BuildOutputLineItem_workcenterId_fkey";
            columns: ["workcenterId"];
            isOneToOne: false;
            referencedRelation: "Workcenter";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "BuildOutputLineItem_workerId_fkey";
            columns: ["workerId"];
            isOneToOne: false;
            referencedRelation: "Worker";
            referencedColumns: ["id"];
          },
        ];
      };
      Channel: {
        Row: {
          deleted: boolean;
          id: number;
          name: string;
          syncTimestamp: string;
          syncToken: number;
        };
        Insert: {
          deleted: boolean;
          id: number;
          name: string;
          syncTimestamp: string;
          syncToken: number;
        };
        Update: {
          deleted?: boolean;
          id?: number;
          name?: string;
          syncTimestamp?: string;
          syncToken?: number;
        };
        Relationships: [];
      };
      Class: {
        Row: {
          deleted: boolean;
          fullName: string;
          id: number;
          name: string;
          syncTimestamp: string;
          syncToken: number;
        };
        Insert: {
          deleted: boolean;
          fullName: string;
          id: number;
          name: string;
          syncTimestamp: string;
          syncToken: number;
        };
        Update: {
          deleted?: boolean;
          fullName?: string;
          id?: number;
          name?: string;
          syncTimestamp?: string;
          syncToken?: number;
        };
        Relationships: [];
      };
      Currency: {
        Row: {
          code: string;
          deleted: boolean;
          id: number;
          syncTimestamp: string;
          syncToken: number;
        };
        Insert: {
          code: string;
          deleted: boolean;
          id: number;
          syncTimestamp: string;
          syncToken: number;
        };
        Update: {
          code?: string;
          deleted?: boolean;
          id?: number;
          syncTimestamp?: string;
          syncToken?: number;
        };
        Relationships: [];
      };
      Customer: {
        Row: {
          altAddresses: SosTypes.Address[];
          altPhone: string | null;
          archived: boolean;
          billing: SosTypes.Address;
          billWithParent: boolean;
          businessLicense: string | null;
          companyName: string | null;
          contact: SosTypes.Contact;
          contractorNumber: string | null;
          creditHold: boolean;
          currencyId: number | null;
          customerTypeId: number | null;
          deleted: boolean;
          email: string | null;
          fax: string | null;
          foundUsVia: string | null;
          fullname: string;
          hasChildren: boolean;
          id: number;
          mobile: string | null;
          name: string;
          notes: string | null;
          parentId: number | null;
          paymentMethodId: number | null;
          phone: string | null;
          priceTierId: number | null;
          resaleNumber: string | null;
          salesRepId: number | null;
          shipping: SosTypes.Address;
          starred: number;
          sublevel: number;
          syncTimestamp: string;
          syncToken: number;
          taxable: boolean | null;
          taxCodeId: number | null;
          termsId: number | null;
          website: string | null;
        };
        Insert: {
          altAddresses: SosTypes.Address[];
          altPhone?: string | null;
          archived: boolean;
          billing: SosTypes.Address;
          billWithParent: boolean;
          businessLicense?: string | null;
          companyName?: string | null;
          contact?: SosTypes.Contact;
          contractorNumber?: string | null;
          creditHold: boolean;
          currencyId?: number | null;
          customerTypeId?: number | null;
          deleted: boolean;
          email?: string | null;
          fax?: string | null;
          foundUsVia?: string | null;
          fullname: string;
          hasChildren: boolean;
          id: number;
          mobile?: string | null;
          name: string;
          notes?: string | null;
          parentId?: number | null;
          paymentMethodId?: number | null;
          phone?: string | null;
          priceTierId?: number | null;
          resaleNumber?: string | null;
          salesRepId?: number | null;
          shipping: SosTypes.Address;
          starred: number;
          sublevel: number;
          syncTimestamp: string;
          syncToken: number;
          taxable?: boolean | null;
          taxCodeId?: number | null;
          termsId?: number | null;
          website?: string | null;
        };
        Update: {
          altAddresses?: SosTypes.Address[];
          altPhone?: string | null;
          archived?: boolean;
          billing?: SosTypes.Address;
          billWithParent?: boolean;
          businessLicense?: string | null;
          companyName?: string | null;
          contact?: SosTypes.Contact;
          contractorNumber?: string | null;
          creditHold?: boolean;
          currencyId?: number | null;
          customerTypeId?: number | null;
          deleted?: boolean;
          email?: string | null;
          fax?: string | null;
          foundUsVia?: string | null;
          fullname?: string;
          hasChildren?: boolean;
          id?: number;
          mobile?: string | null;
          name?: string;
          notes?: string | null;
          parentId?: number | null;
          paymentMethodId?: number | null;
          phone?: string | null;
          priceTierId?: number | null;
          resaleNumber?: string | null;
          salesRepId?: number | null;
          shipping?: SosTypes.Address;
          starred?: number;
          sublevel?: number;
          syncTimestamp?: string;
          syncToken?: number;
          taxable?: boolean | null;
          taxCodeId?: number | null;
          termsId?: number | null;
          website?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "Customer_currencyId_fkey";
            columns: ["currencyId"];
            isOneToOne: false;
            referencedRelation: "Currency";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "Customer_parentId_fkey";
            columns: ["parentId"];
            isOneToOne: false;
            referencedRelation: "Customer";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "Customer_paymentMethodId_fkey";
            columns: ["paymentMethodId"];
            isOneToOne: false;
            referencedRelation: "PaymentMethod";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "Customer_priceTierId_fkey";
            columns: ["priceTierId"];
            isOneToOne: false;
            referencedRelation: "PriceTier";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "Customer_salesRepId_fkey";
            columns: ["salesRepId"];
            isOneToOne: false;
            referencedRelation: "SalesRep";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "Customer_taxCodeId_fkey";
            columns: ["taxCodeId"];
            isOneToOne: false;
            referencedRelation: "TaxCode";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "Customer_termsId_fkey";
            columns: ["termsId"];
            isOneToOne: false;
            referencedRelation: "Terms";
            referencedColumns: ["id"];
          },
        ];
      };
      CustomFieldDefinition: {
        Row: {
          dataType: Database["public"]["Enums"]["CustomFieldDataType"];
          deleted: boolean;
          description: string | null;
          id: number;
          listValues: string | null;
          name: string;
          showOn: string;
          syncTimestamp: string;
          syncToken: number;
        };
        Insert: {
          dataType: Database["public"]["Enums"]["CustomFieldDataType"];
          deleted: boolean;
          description?: string | null;
          id: number;
          listValues?: string | null;
          name: string;
          showOn: string;
          syncTimestamp: string;
          syncToken: number;
        };
        Update: {
          dataType?: Database["public"]["Enums"]["CustomFieldDataType"];
          deleted?: boolean;
          description?: string | null;
          id?: number;
          listValues?: string | null;
          name?: string;
          showOn?: string;
          syncTimestamp?: string;
          syncToken?: number;
        };
        Relationships: [];
      };
      Department: {
        Row: {
          deleted: boolean;
          id: number;
          name: string;
          syncTimestamp: string;
          syncToken: number;
        };
        Insert: {
          deleted: boolean;
          id: number;
          name: string;
          syncTimestamp: string;
          syncToken: number;
        };
        Update: {
          deleted?: boolean;
          id?: number;
          name?: string;
          syncTimestamp?: string;
          syncToken?: number;
        };
        Relationships: [];
      };
      Estimate: {
        Row: {
          acceptedBy: string | null;
          acceptedDate: string | null;
          archived: boolean;
          billing: SosTypes.TransactionAddress | null;
          channelId: number | null;
          comment: string | null;
          currencyId: number | null;
          customerId: number;
          customerMessage: string | null;
          date: string;
          deleted: boolean;
          departmentId: number | null;
          discountAmount: number;
          discountPercent: number;
          discountTaxable: boolean;
          exchangeRate: number;
          expiration: string | null;
          id: number;
          number: string;
          salesRepId: number | null;
          shipping: SosTypes.TransactionAddress | null;
          shippingAmount: number;
          shippingTaxable: boolean;
          starred: number;
          status: Database["public"]["Enums"]["EstimateStatus"];
          subTotal: number;
          syncTimestamp: string;
          syncToken: number;
          taxAmount: number;
          taxCodeId: number | null;
          taxPercent: number;
          total: number;
        };
        Insert: {
          acceptedBy?: string | null;
          acceptedDate?: string | null;
          archived: boolean;
          billing?: SosTypes.TransactionAddress | null;
          channelId?: number | null;
          comment?: string | null;
          currencyId?: number | null;
          customerId: number;
          customerMessage?: string | null;
          date: string;
          deleted: boolean;
          departmentId?: number | null;
          discountAmount: number;
          discountPercent: number;
          discountTaxable: boolean;
          exchangeRate: number;
          expiration?: string | null;
          id: number;
          number: string;
          salesRepId?: number | null;
          shipping?: SosTypes.TransactionAddress | null;
          shippingAmount: number;
          shippingTaxable: boolean;
          starred: number;
          status: Database["public"]["Enums"]["EstimateStatus"];
          subTotal: number;
          syncTimestamp: string;
          syncToken: number;
          taxAmount: number;
          taxCodeId?: number | null;
          taxPercent: number;
          total: number;
        };
        Update: {
          acceptedBy?: string | null;
          acceptedDate?: string | null;
          archived?: boolean;
          billing?: SosTypes.TransactionAddress | null;
          channelId?: number | null;
          comment?: string | null;
          currencyId?: number | null;
          customerId?: number;
          customerMessage?: string | null;
          date?: string;
          deleted?: boolean;
          departmentId?: number | null;
          discountAmount?: number;
          discountPercent?: number;
          discountTaxable?: boolean;
          exchangeRate?: number;
          expiration?: string | null;
          id?: number;
          number?: string;
          salesRepId?: number | null;
          shipping?: SosTypes.TransactionAddress | null;
          shippingAmount?: number;
          shippingTaxable?: boolean;
          starred?: number;
          status?: Database["public"]["Enums"]["EstimateStatus"];
          subTotal?: number;
          syncTimestamp?: string;
          syncToken?: number;
          taxAmount?: number;
          taxCodeId?: number | null;
          taxPercent?: number;
          total?: number;
        };
        Relationships: [
          {
            foreignKeyName: "Estimate_channelId_fkey";
            columns: ["channelId"];
            isOneToOne: false;
            referencedRelation: "Channel";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "Estimate_currencyId_fkey";
            columns: ["currencyId"];
            isOneToOne: false;
            referencedRelation: "Currency";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "Estimate_customerId_fkey";
            columns: ["customerId"];
            isOneToOne: false;
            referencedRelation: "Customer";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "Estimate_departmentId_fkey";
            columns: ["departmentId"];
            isOneToOne: false;
            referencedRelation: "Department";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "Estimate_salesRepId_fkey";
            columns: ["salesRepId"];
            isOneToOne: false;
            referencedRelation: "SalesRep";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "Estimate_taxCodeId_fkey";
            columns: ["taxCodeId"];
            isOneToOne: false;
            referencedRelation: "TaxCode";
            referencedColumns: ["id"];
          },
        ];
      };
      EstimateCustomFieldValue: {
        Row: {
          customFieldDefinitionId: number;
          deleted: boolean;
          estimateId: number;
          syncTimestamp: string;
          value: string | null;
        };
        Insert: {
          customFieldDefinitionId: number;
          deleted: boolean;
          estimateId: number;
          syncTimestamp: string;
          value?: string | null;
        };
        Update: {
          customFieldDefinitionId?: number;
          deleted?: boolean;
          estimateId?: number;
          syncTimestamp?: string;
          value?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "EstimateCustomFieldValue_customFieldDefinitionId_fkey";
            columns: ["customFieldDefinitionId"];
            isOneToOne: false;
            referencedRelation: "CustomFieldDefinition";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "EstimateCustomFieldValue_estimateId_fkey";
            columns: ["estimateId"];
            isOneToOne: false;
            referencedRelation: "Estimate";
            referencedColumns: ["id"];
          },
        ];
      };
      EstimateLineItem: {
        Row: {
          amount: number;
          classId: number | null;
          cost: number | null;
          deleted: boolean;
          description: string | null;
          estimateId: number;
          id: number;
          itemId: number;
          jobId: number | null;
          lineNumber: number;
          listPrice: number | null;
          margin: number | null;
          percentdiscount: number | null;
          quantity: number;
          syncTimestamp: string;
          taxable: boolean;
          taxCodeId: number | null;
          unitprice: number;
          uomId: number | null;
          volume: number;
          volumeunit: string | null;
          weight: number;
          weightunit: string | null;
          workcenterId: number | null;
        };
        Insert: {
          amount: number;
          classId?: number | null;
          cost?: number | null;
          deleted: boolean;
          description?: string | null;
          estimateId: number;
          id: number;
          itemId: number;
          jobId?: number | null;
          lineNumber: number;
          listPrice?: number | null;
          margin?: number | null;
          percentdiscount?: number | null;
          quantity: number;
          syncTimestamp: string;
          taxable: boolean;
          taxCodeId?: number | null;
          unitprice: number;
          uomId?: number | null;
          volume: number;
          volumeunit?: string | null;
          weight: number;
          weightunit?: string | null;
          workcenterId?: number | null;
        };
        Update: {
          amount?: number;
          classId?: number | null;
          cost?: number | null;
          deleted?: boolean;
          description?: string | null;
          estimateId?: number;
          id?: number;
          itemId?: number;
          jobId?: number | null;
          lineNumber?: number;
          listPrice?: number | null;
          margin?: number | null;
          percentdiscount?: number | null;
          quantity?: number;
          syncTimestamp?: string;
          taxable?: boolean;
          taxCodeId?: number | null;
          unitprice?: number;
          uomId?: number | null;
          volume?: number;
          volumeunit?: string | null;
          weight?: number;
          weightunit?: string | null;
          workcenterId?: number | null;
        };
        Relationships: [
          {
            foreignKeyName: "EstimateLineItem_classId_fkey";
            columns: ["classId"];
            isOneToOne: false;
            referencedRelation: "Class";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "EstimateLineItem_estimateId_fkey";
            columns: ["estimateId"];
            isOneToOne: false;
            referencedRelation: "Estimate";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "EstimateLineItem_itemId_fkey";
            columns: ["itemId"];
            isOneToOne: false;
            referencedRelation: "Item";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "EstimateLineItem_itemId_uomId_fkey";
            columns: ["itemId", "uomId"];
            isOneToOne: false;
            referencedRelation: "ItemUOM";
            referencedColumns: ["itemId", "uomId"];
          },
          {
            foreignKeyName: "EstimateLineItem_jobId_fkey";
            columns: ["jobId"];
            isOneToOne: false;
            referencedRelation: "Job";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "EstimateLineItem_taxCodeId_fkey";
            columns: ["taxCodeId"];
            isOneToOne: false;
            referencedRelation: "TaxCode";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "EstimateLineItem_uomId_fkey";
            columns: ["uomId"];
            isOneToOne: false;
            referencedRelation: "UOM";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "EstimateLineItem_workcenterId_fkey";
            columns: ["workcenterId"];
            isOneToOne: false;
            referencedRelation: "Workcenter";
            referencedColumns: ["id"];
          },
        ];
      };
      Item: {
        Row: {
          alwaysShippable: boolean;
          archived: boolean;
          assetAccountId: number | null;
          available: number;
          barcode: string | null;
          basePurchaseCost: number | null;
          baseSalesPrice: number;
          binId: number | null;
          category: string | null;
          classId: number | null;
          cogsAccountId: number | null;
          commissionAmount: number | null;
          commissionExempt: boolean;
          commissionRate: number | null;
          costBasis: number;
          customerPartNumber: string | null;
          deleted: boolean;
          description: string | null;
          expenseAccountId: number | null;
          fullname: string;
          hasImage: boolean;
          hasVariants: boolean;
          id: number;
          incomeAccountId: number | null;
          leadTime: number | null;
          lotTracking: boolean;
          markupPercent: number | null;
          maxStock: number | null;
          minimumPrice: number | null;
          name: string;
          notes: string | null;
          onhand: number;
          onPO: number;
          onRMA: number;
          onSO: number;
          onSR: number;
          onWO: number;
          purchaseCost: number;
          purchaseDescription: string | null;
          purchaseTaxCodeId: number | null;
          rented: number;
          reorderPoint: number | null;
          salesPrice: number;
          salesTaxCodeId: number | null;
          serialTracking: boolean;
          showOnManufacturingForms: boolean;
          showOnPurchasingForms: boolean;
          showOnSalesForms: boolean;
          sku: string | null;
          starred: number;
          sublevel: number;
          syncTimestamp: string;
          syncToken: number;
          tags: string | null;
          taxable: boolean;
          type: Database["public"]["Enums"]["ItemType"];
          url: string | null;
          useMarkup: boolean | null;
          vendorId: number | null;
          vendorPartNumber: string | null;
          volume: number | null;
          volumeUnit: string | null;
          warranty: string | null;
          weight: number | null;
          weightUnit: string | null;
        };
        Insert: {
          alwaysShippable: boolean;
          archived: boolean;
          assetAccountId?: number | null;
          available: number;
          barcode?: string | null;
          basePurchaseCost?: number | null;
          baseSalesPrice: number;
          binId?: number | null;
          category?: string | null;
          classId?: number | null;
          cogsAccountId?: number | null;
          commissionAmount?: number | null;
          commissionExempt: boolean;
          commissionRate?: number | null;
          costBasis: number;
          customerPartNumber?: string | null;
          deleted: boolean;
          description?: string | null;
          expenseAccountId?: number | null;
          fullname: string;
          hasImage: boolean;
          hasVariants: boolean;
          id: number;
          incomeAccountId?: number | null;
          leadTime?: number | null;
          lotTracking: boolean;
          markupPercent?: number | null;
          maxStock?: number | null;
          minimumPrice?: number | null;
          name: string;
          notes?: string | null;
          onhand: number;
          onPO: number;
          onRMA: number;
          onSO: number;
          onSR: number;
          onWO: number;
          purchaseCost: number;
          purchaseDescription?: string | null;
          purchaseTaxCodeId?: number | null;
          rented: number;
          reorderPoint?: number | null;
          salesPrice: number;
          salesTaxCodeId?: number | null;
          serialTracking: boolean;
          showOnManufacturingForms: boolean;
          showOnPurchasingForms: boolean;
          showOnSalesForms: boolean;
          sku?: string | null;
          starred: number;
          sublevel: number;
          syncTimestamp: string;
          syncToken: number;
          tags?: string | null;
          taxable: boolean;
          type: Database["public"]["Enums"]["ItemType"];
          url?: string | null;
          useMarkup?: boolean | null;
          vendorId?: number | null;
          vendorPartNumber?: string | null;
          volume?: number | null;
          volumeUnit?: string | null;
          warranty?: string | null;
          weight?: number | null;
          weightUnit?: string | null;
        };
        Update: {
          alwaysShippable?: boolean;
          archived?: boolean;
          assetAccountId?: number | null;
          available?: number;
          barcode?: string | null;
          basePurchaseCost?: number | null;
          baseSalesPrice?: number;
          binId?: number | null;
          category?: string | null;
          classId?: number | null;
          cogsAccountId?: number | null;
          commissionAmount?: number | null;
          commissionExempt?: boolean;
          commissionRate?: number | null;
          costBasis?: number;
          customerPartNumber?: string | null;
          deleted?: boolean;
          description?: string | null;
          expenseAccountId?: number | null;
          fullname?: string;
          hasImage?: boolean;
          hasVariants?: boolean;
          id?: number;
          incomeAccountId?: number | null;
          leadTime?: number | null;
          lotTracking?: boolean;
          markupPercent?: number | null;
          maxStock?: number | null;
          minimumPrice?: number | null;
          name?: string;
          notes?: string | null;
          onhand?: number;
          onPO?: number;
          onRMA?: number;
          onSO?: number;
          onSR?: number;
          onWO?: number;
          purchaseCost?: number;
          purchaseDescription?: string | null;
          purchaseTaxCodeId?: number | null;
          rented?: number;
          reorderPoint?: number | null;
          salesPrice?: number;
          salesTaxCodeId?: number | null;
          serialTracking?: boolean;
          showOnManufacturingForms?: boolean;
          showOnPurchasingForms?: boolean;
          showOnSalesForms?: boolean;
          sku?: string | null;
          starred?: number;
          sublevel?: number;
          syncTimestamp?: string;
          syncToken?: number;
          tags?: string | null;
          taxable?: boolean;
          type?: Database["public"]["Enums"]["ItemType"];
          url?: string | null;
          useMarkup?: boolean | null;
          vendorId?: number | null;
          vendorPartNumber?: string | null;
          volume?: number | null;
          volumeUnit?: string | null;
          warranty?: string | null;
          weight?: number | null;
          weightUnit?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "Item_assetAccountId_fkey";
            columns: ["assetAccountId"];
            isOneToOne: false;
            referencedRelation: "Account";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "Item_binId_fkey";
            columns: ["binId"];
            isOneToOne: false;
            referencedRelation: "Bin";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "Item_classId_fkey";
            columns: ["classId"];
            isOneToOne: false;
            referencedRelation: "Class";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "Item_cogsAccountId_fkey";
            columns: ["cogsAccountId"];
            isOneToOne: false;
            referencedRelation: "Account";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "Item_expenseAccountId_fkey";
            columns: ["expenseAccountId"];
            isOneToOne: false;
            referencedRelation: "Account";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "Item_incomeAccountId_fkey";
            columns: ["incomeAccountId"];
            isOneToOne: false;
            referencedRelation: "Account";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "Item_purchaseTaxCodeId_fkey";
            columns: ["purchaseTaxCodeId"];
            isOneToOne: false;
            referencedRelation: "TaxCode";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "Item_salesTaxCodeId_fkey";
            columns: ["salesTaxCodeId"];
            isOneToOne: false;
            referencedRelation: "TaxCode";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "Item_vendorId_fkey";
            columns: ["vendorId"];
            isOneToOne: false;
            referencedRelation: "Vendor";
            referencedColumns: ["id"];
          },
        ];
      };
      ItemCustomFieldValue: {
        Row: {
          customFieldDefinitionId: number;
          deleted: boolean;
          itemId: number;
          syncTimestamp: string;
          value: string | null;
        };
        Insert: {
          customFieldDefinitionId: number;
          deleted: boolean;
          itemId: number;
          syncTimestamp: string;
          value?: string | null;
        };
        Update: {
          customFieldDefinitionId?: number;
          deleted?: boolean;
          itemId?: number;
          syncTimestamp?: string;
          value?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "ItemCustomFieldValue_customFieldDefinitionId_fkey";
            columns: ["customFieldDefinitionId"];
            isOneToOne: false;
            referencedRelation: "CustomFieldDefinition";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "ItemCustomFieldValue_itemId_fkey";
            columns: ["itemId"];
            isOneToOne: false;
            referencedRelation: "Item";
            referencedColumns: ["id"];
          },
        ];
      };
      ItemReceipt: {
        Row: {
          archived: boolean;
          comment: string | null;
          currencyId: number;
          date: string;
          deleted: boolean;
          departmentId: number | null;
          depositAmount: number;
          exchangeRate: number;
          id: number;
          locationId: number | null;
          number: string;
          overhead: number;
          payment: string;
          starred: number;
          subTotal: number;
          syncTimestamp: string;
          syncToken: number;
          taxAmount: number;
          taxCodeId: number | null;
          termsId: number | null;
          total: number;
          vendorId: number;
          vendorMessage: string | null;
        };
        Insert: {
          archived: boolean;
          comment?: string | null;
          currencyId: number;
          date: string;
          deleted: boolean;
          departmentId?: number | null;
          depositAmount: number;
          exchangeRate: number;
          id: number;
          locationId?: number | null;
          number: string;
          overhead: number;
          payment: string;
          starred: number;
          subTotal: number;
          syncTimestamp: string;
          syncToken: number;
          taxAmount: number;
          taxCodeId?: number | null;
          termsId?: number | null;
          total: number;
          vendorId: number;
          vendorMessage?: string | null;
        };
        Update: {
          archived?: boolean;
          comment?: string | null;
          currencyId?: number;
          date?: string;
          deleted?: boolean;
          departmentId?: number | null;
          depositAmount?: number;
          exchangeRate?: number;
          id?: number;
          locationId?: number | null;
          number?: string;
          overhead?: number;
          payment?: string;
          starred?: number;
          subTotal?: number;
          syncTimestamp?: string;
          syncToken?: number;
          taxAmount?: number;
          taxCodeId?: number | null;
          termsId?: number | null;
          total?: number;
          vendorId?: number;
          vendorMessage?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "ItemReceipt_currencyId_fkey";
            columns: ["currencyId"];
            isOneToOne: false;
            referencedRelation: "Currency";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "ItemReceipt_departmentId_fkey";
            columns: ["departmentId"];
            isOneToOne: false;
            referencedRelation: "Department";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "ItemReceipt_locationId_fkey";
            columns: ["locationId"];
            isOneToOne: false;
            referencedRelation: "Location";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "ItemReceipt_taxCodeId_fkey";
            columns: ["taxCodeId"];
            isOneToOne: false;
            referencedRelation: "TaxCode";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "ItemReceipt_termsId_fkey";
            columns: ["termsId"];
            isOneToOne: false;
            referencedRelation: "Terms";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "ItemReceipt_vendorId_fkey";
            columns: ["vendorId"];
            isOneToOne: false;
            referencedRelation: "Vendor";
            referencedColumns: ["id"];
          },
        ];
      };
      ItemReceiptCustomFieldValue: {
        Row: {
          customFieldDefinitionId: number;
          deleted: boolean;
          itemReceiptId: number;
          syncTimestamp: string;
          value: string | null;
        };
        Insert: {
          customFieldDefinitionId: number;
          deleted: boolean;
          itemReceiptId: number;
          syncTimestamp: string;
          value?: string | null;
        };
        Update: {
          customFieldDefinitionId?: number;
          deleted?: boolean;
          itemReceiptId?: number;
          syncTimestamp?: string;
          value?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "ItemReceiptCustomFieldValue_customFieldDefinitionId_fkey";
            columns: ["customFieldDefinitionId"];
            isOneToOne: false;
            referencedRelation: "CustomFieldDefinition";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "ItemReceiptCustomFieldValue_itemReceiptId_fkey";
            columns: ["itemReceiptId"];
            isOneToOne: false;
            referencedRelation: "ItemReceipt";
            referencedColumns: ["id"];
          },
        ];
      };
      ItemReceiptLineItem: {
        Row: {
          amount: number;
          binId: number | null;
          classId: number | null;
          customerId: number | null;
          deleted: boolean;
          description: string | null;
          id: number;
          itemId: number;
          itemReceiptId: number;
          jobId: number | null;
          lineNumber: number;
          linkedPurchaseOrderLineItemId: number | null;
          lotId: number | null;
          quantity: number;
          received: number;
          serials: SosTypes.Reference[] | null;
          syncTimestamp: string;
          taxable: boolean;
          taxCodeId: number | null;
          unitprice: number;
          uomId: number | null;
          vendorPartNumber: string | null;
          volume: number;
          volumeunit: string | null;
          weight: number;
          weightunit: string | null;
          workcenterId: number | null;
        };
        Insert: {
          amount: number;
          binId?: number | null;
          classId?: number | null;
          customerId?: number | null;
          deleted: boolean;
          description?: string | null;
          id: number;
          itemId: number;
          itemReceiptId: number;
          jobId?: number | null;
          lineNumber: number;
          linkedPurchaseOrderLineItemId?: number | null;
          lotId?: number | null;
          quantity: number;
          received: number;
          serials?: SosTypes.Reference[] | null;
          syncTimestamp: string;
          taxable: boolean;
          taxCodeId?: number | null;
          unitprice: number;
          uomId?: number | null;
          vendorPartNumber?: string | null;
          volume: number;
          volumeunit?: string | null;
          weight: number;
          weightunit?: string | null;
          workcenterId?: number | null;
        };
        Update: {
          amount?: number;
          binId?: number | null;
          classId?: number | null;
          customerId?: number | null;
          deleted?: boolean;
          description?: string | null;
          id?: number;
          itemId?: number;
          itemReceiptId?: number;
          jobId?: number | null;
          lineNumber?: number;
          linkedPurchaseOrderLineItemId?: number | null;
          lotId?: number | null;
          quantity?: number;
          received?: number;
          serials?: SosTypes.Reference[] | null;
          syncTimestamp?: string;
          taxable?: boolean;
          taxCodeId?: number | null;
          unitprice?: number;
          uomId?: number | null;
          vendorPartNumber?: string | null;
          volume?: number;
          volumeunit?: string | null;
          weight?: number;
          weightunit?: string | null;
          workcenterId?: number | null;
        };
        Relationships: [
          {
            foreignKeyName: "ItemReceiptLineItem_binId_fkey";
            columns: ["binId"];
            isOneToOne: false;
            referencedRelation: "Bin";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "ItemReceiptLineItem_classId_fkey";
            columns: ["classId"];
            isOneToOne: false;
            referencedRelation: "Class";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "ItemReceiptLineItem_customerId_fkey";
            columns: ["customerId"];
            isOneToOne: false;
            referencedRelation: "Customer";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "ItemReceiptLineItem_itemId_fkey";
            columns: ["itemId"];
            isOneToOne: false;
            referencedRelation: "Item";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "ItemReceiptLineItem_itemId_uomId_fkey";
            columns: ["itemId", "uomId"];
            isOneToOne: false;
            referencedRelation: "ItemUOM";
            referencedColumns: ["itemId", "uomId"];
          },
          {
            foreignKeyName: "ItemReceiptLineItem_itemReceiptId_fkey";
            columns: ["itemReceiptId"];
            isOneToOne: false;
            referencedRelation: "ItemReceipt";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "ItemReceiptLineItem_jobId_fkey";
            columns: ["jobId"];
            isOneToOne: false;
            referencedRelation: "Job";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "ItemReceiptLineItem_linkedPurchaseOrderLineItemId_fkey";
            columns: ["linkedPurchaseOrderLineItemId"];
            isOneToOne: false;
            referencedRelation: "PurchaseOrderLineItem";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "ItemReceiptLineItem_taxCodeId_fkey";
            columns: ["taxCodeId"];
            isOneToOne: false;
            referencedRelation: "TaxCode";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "ItemReceiptLineItem_uomId_fkey";
            columns: ["uomId"];
            isOneToOne: false;
            referencedRelation: "UOM";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "ItemReceiptLineItem_workcenterId_fkey";
            columns: ["workcenterId"];
            isOneToOne: false;
            referencedRelation: "Workcenter";
            referencedColumns: ["id"];
          },
        ];
      };
      ItemUOM: {
        Row: {
          conversion: number;
          deleted: boolean;
          isBase: boolean;
          itemId: number;
          purchaseCost: number | null;
          salesPrice: number | null;
          syncTimestamp: string;
          uomId: number;
        };
        Insert: {
          conversion: number;
          deleted: boolean;
          isBase: boolean;
          itemId: number;
          purchaseCost?: number | null;
          salesPrice?: number | null;
          syncTimestamp: string;
          uomId: number;
        };
        Update: {
          conversion?: number;
          deleted?: boolean;
          isBase?: boolean;
          itemId?: number;
          purchaseCost?: number | null;
          salesPrice?: number | null;
          syncTimestamp?: string;
          uomId?: number;
        };
        Relationships: [
          {
            foreignKeyName: "ItemUOM_itemId_fkey";
            columns: ["itemId"];
            isOneToOne: false;
            referencedRelation: "Item";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "ItemUOM_uomId_fkey";
            columns: ["uomId"];
            isOneToOne: false;
            referencedRelation: "UOM";
            referencedColumns: ["id"];
          },
        ];
      };
      Job: {
        Row: {
          address: SosTypes.Address;
          archived: boolean;
          closed: boolean;
          customerId: number | null;
          deleted: boolean;
          description: string | null;
          id: number;
          name: string;
          number: string;
          starred: number;
          syncTimestamp: string;
          syncToken: number;
        };
        Insert: {
          address: SosTypes.Address;
          archived: boolean;
          closed: boolean;
          customerId?: number | null;
          deleted: boolean;
          description?: string | null;
          id: number;
          name: string;
          number: string;
          starred: number;
          syncTimestamp: string;
          syncToken: number;
        };
        Update: {
          address?: SosTypes.Address;
          archived?: boolean;
          closed?: boolean;
          customerId?: number | null;
          deleted?: boolean;
          description?: string | null;
          id?: number;
          name?: string;
          number?: string;
          starred?: number;
          syncTimestamp?: string;
          syncToken?: number;
        };
        Relationships: [
          {
            foreignKeyName: "Job_customerId_fkey";
            columns: ["customerId"];
            isOneToOne: false;
            referencedRelation: "Customer";
            referencedColumns: ["id"];
          },
        ];
      };
      JobCustomFieldValue: {
        Row: {
          customFieldDefinitionId: number;
          deleted: boolean;
          jobId: number;
          syncTimestamp: string;
          value: string | null;
        };
        Insert: {
          customFieldDefinitionId: number;
          deleted: boolean;
          jobId: number;
          syncTimestamp: string;
          value?: string | null;
        };
        Update: {
          customFieldDefinitionId?: number;
          deleted?: boolean;
          jobId?: number;
          syncTimestamp?: string;
          value?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "JobCustomFieldValue_customFieldDefinitionId_fkey";
            columns: ["customFieldDefinitionId"];
            isOneToOne: false;
            referencedRelation: "CustomFieldDefinition";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "JobCustomFieldValue_jobId_fkey";
            columns: ["jobId"];
            isOneToOne: false;
            referencedRelation: "Job";
            referencedColumns: ["id"];
          },
        ];
      };
      JobWorkcenter: {
        Row: {
          deleted: boolean;
          jobId: number;
          sequence: number;
          syncTimestamp: string;
          workcenterId: number;
        };
        Insert: {
          deleted: boolean;
          jobId: number;
          sequence: number;
          syncTimestamp: string;
          workcenterId: number;
        };
        Update: {
          deleted?: boolean;
          jobId?: number;
          sequence?: number;
          syncTimestamp?: string;
          workcenterId?: number;
        };
        Relationships: [
          {
            foreignKeyName: "JobWorkcenter_jobId_fkey";
            columns: ["jobId"];
            isOneToOne: false;
            referencedRelation: "Job";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "JobWorkcenter_workcenterId_fkey";
            columns: ["workcenterId"];
            isOneToOne: false;
            referencedRelation: "Workcenter";
            referencedColumns: ["id"];
          },
        ];
      };
      Location: {
        Row: {
          address: SosTypes.Address;
          binTracking: boolean;
          company: string | null;
          contact: string | null;
          defaultLocation: boolean;
          deleted: boolean;
          email: string | null;
          id: number;
          name: string;
          nonNettable: boolean;
          phone: string | null;
          salesTaxRate: number;
          shippingTaxable: boolean;
          syncTimestamp: string;
          syncToken: number;
        };
        Insert: {
          address?: SosTypes.Address;
          binTracking: boolean;
          company?: string | null;
          contact?: string | null;
          defaultLocation: boolean;
          deleted: boolean;
          email?: string | null;
          id: number;
          name: string;
          nonNettable: boolean;
          phone?: string | null;
          salesTaxRate: number;
          shippingTaxable: boolean;
          syncTimestamp: string;
          syncToken: number;
        };
        Update: {
          address?: SosTypes.Address;
          binTracking?: boolean;
          company?: string | null;
          contact?: string | null;
          defaultLocation?: boolean;
          deleted?: boolean;
          email?: string | null;
          id?: number;
          name?: string;
          nonNettable?: boolean;
          phone?: string | null;
          salesTaxRate?: number;
          shippingTaxable?: boolean;
          syncTimestamp?: string;
          syncToken?: number;
        };
        Relationships: [];
      };
      LongRunningGasRequest: {
        Row: {
          apiMethod: string;
          createdAt: string;
          id: string;
          progress: string | null;
          result: Json | null;
          status: Database["public"]["Enums"]["LongRunningGasRequestStatus"];
          updatedAt: string | null;
        };
        Insert: {
          apiMethod: string;
          createdAt?: string;
          id: string;
          progress?: string | null;
          result?: Json | null;
          status: Database["public"]["Enums"]["LongRunningGasRequestStatus"];
          updatedAt?: string | null;
        };
        Update: {
          apiMethod?: string;
          createdAt?: string;
          id?: string;
          progress?: string | null;
          result?: Json | null;
          status?: Database["public"]["Enums"]["LongRunningGasRequestStatus"];
          updatedAt?: string | null;
        };
        Relationships: [];
      };
      OrderStage: {
        Row: {
          deleted: boolean;
          id: number;
          name: string;
          syncTimestamp: string;
          syncToken: number;
        };
        Insert: {
          deleted: boolean;
          id: number;
          name: string;
          syncTimestamp: string;
          syncToken: number;
        };
        Update: {
          deleted?: boolean;
          id?: number;
          name?: string;
          syncTimestamp?: string;
          syncToken?: number;
        };
        Relationships: [];
      };
      PaymentMethod: {
        Row: {
          deleted: boolean;
          id: number;
          name: string;
          syncTimestamp: string;
          syncToken: number;
        };
        Insert: {
          deleted: boolean;
          id: number;
          name: string;
          syncTimestamp: string;
          syncToken: number;
        };
        Update: {
          deleted?: boolean;
          id?: number;
          name?: string;
          syncTimestamp?: string;
          syncToken?: number;
        };
        Relationships: [];
      };
      PriceTier: {
        Row: {
          deleted: boolean;
          id: number;
          name: string;
          percentDiscount: number;
          syncTimestamp: string;
          syncToken: number;
        };
        Insert: {
          deleted: boolean;
          id: number;
          name: string;
          percentDiscount: number;
          syncTimestamp: string;
          syncToken: number;
        };
        Update: {
          deleted?: boolean;
          id?: number;
          name?: string;
          percentDiscount?: number;
          syncTimestamp?: string;
          syncToken?: number;
        };
        Relationships: [];
      };
      Priority: {
        Row: {
          deleted: boolean;
          id: number;
          name: string;
          sortOrder: number;
          syncTimestamp: string;
          syncToken: number;
        };
        Insert: {
          deleted: boolean;
          id: number;
          name: string;
          sortOrder: number;
          syncTimestamp: string;
          syncToken: number;
        };
        Update: {
          deleted?: boolean;
          id?: number;
          name?: string;
          sortOrder?: number;
          syncTimestamp?: string;
          syncToken?: number;
        };
        Relationships: [];
      };
      PurchaseOrder: {
        Row: {
          archived: boolean;
          assignedToUserId: number | null;
          billing: SosTypes.TransactionAddress | null;
          blanketPO: boolean;
          closed: boolean;
          comment: string | null;
          confirmed: boolean;
          contractManufacturing: boolean;
          currencyId: number;
          customerId: number | null;
          date: string;
          deleted: boolean;
          departmentId: number | null;
          depositAmount: number;
          dropShip: boolean;
          exchangeRate: number;
          expectedDate: string | null;
          expectedShip: string | null;
          id: number;
          locationId: number | null;
          number: string;
          openAmount: number;
          pendingApproval: boolean;
          receivedStatus: Database["public"]["Enums"]["ReceivedStatus"];
          shipping: SosTypes.TransactionAddress | null;
          shippingMethodId: number | null;
          starred: number;
          subTotal: number;
          syncTimestamp: string;
          syncToken: number;
          taxAmount: number;
          taxCodeId: number | null;
          termsId: number | null;
          total: number;
          trackingNumber: string | null;
          vendorId: number;
          vendorMessage: string | null;
        };
        Insert: {
          archived: boolean;
          assignedToUserId?: number | null;
          billing?: SosTypes.TransactionAddress | null;
          blanketPO: boolean;
          closed: boolean;
          comment?: string | null;
          confirmed: boolean;
          contractManufacturing: boolean;
          currencyId: number;
          customerId?: number | null;
          date: string;
          deleted: boolean;
          departmentId?: number | null;
          depositAmount: number;
          dropShip: boolean;
          exchangeRate: number;
          expectedDate?: string | null;
          expectedShip?: string | null;
          id: number;
          locationId?: number | null;
          number: string;
          openAmount: number;
          pendingApproval: boolean;
          receivedStatus: Database["public"]["Enums"]["ReceivedStatus"];
          shipping?: SosTypes.TransactionAddress | null;
          shippingMethodId?: number | null;
          starred: number;
          subTotal: number;
          syncTimestamp: string;
          syncToken: number;
          taxAmount: number;
          taxCodeId?: number | null;
          termsId?: number | null;
          total: number;
          trackingNumber?: string | null;
          vendorId: number;
          vendorMessage?: string | null;
        };
        Update: {
          archived?: boolean;
          assignedToUserId?: number | null;
          billing?: SosTypes.TransactionAddress | null;
          blanketPO?: boolean;
          closed?: boolean;
          comment?: string | null;
          confirmed?: boolean;
          contractManufacturing?: boolean;
          currencyId?: number;
          customerId?: number | null;
          date?: string;
          deleted?: boolean;
          departmentId?: number | null;
          depositAmount?: number;
          dropShip?: boolean;
          exchangeRate?: number;
          expectedDate?: string | null;
          expectedShip?: string | null;
          id?: number;
          locationId?: number | null;
          number?: string;
          openAmount?: number;
          pendingApproval?: boolean;
          receivedStatus?: Database["public"]["Enums"]["ReceivedStatus"];
          shipping?: SosTypes.TransactionAddress | null;
          shippingMethodId?: number | null;
          starred?: number;
          subTotal?: number;
          syncTimestamp?: string;
          syncToken?: number;
          taxAmount?: number;
          taxCodeId?: number | null;
          termsId?: number | null;
          total?: number;
          trackingNumber?: string | null;
          vendorId?: number;
          vendorMessage?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "PurchaseOrder_assignedToUserId_fkey";
            columns: ["assignedToUserId"];
            isOneToOne: false;
            referencedRelation: "User";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "PurchaseOrder_currencyId_fkey";
            columns: ["currencyId"];
            isOneToOne: false;
            referencedRelation: "Currency";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "PurchaseOrder_customerId_fkey";
            columns: ["customerId"];
            isOneToOne: false;
            referencedRelation: "Customer";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "PurchaseOrder_departmentId_fkey";
            columns: ["departmentId"];
            isOneToOne: false;
            referencedRelation: "Department";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "PurchaseOrder_locationId_fkey";
            columns: ["locationId"];
            isOneToOne: false;
            referencedRelation: "Location";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "PurchaseOrder_shippingMethodId_fkey";
            columns: ["shippingMethodId"];
            isOneToOne: false;
            referencedRelation: "ShipMethod";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "PurchaseOrder_taxCodeId_fkey";
            columns: ["taxCodeId"];
            isOneToOne: false;
            referencedRelation: "TaxCode";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "PurchaseOrder_termsId_fkey";
            columns: ["termsId"];
            isOneToOne: false;
            referencedRelation: "Terms";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "PurchaseOrder_vendorId_fkey";
            columns: ["vendorId"];
            isOneToOne: false;
            referencedRelation: "Vendor";
            referencedColumns: ["id"];
          },
        ];
      };
      PurchaseOrderCustomFieldValue: {
        Row: {
          customFieldDefinitionId: number;
          deleted: boolean;
          purchaseOrderId: number;
          syncTimestamp: string;
          value: string | null;
        };
        Insert: {
          customFieldDefinitionId: number;
          deleted: boolean;
          purchaseOrderId: number;
          syncTimestamp: string;
          value?: string | null;
        };
        Update: {
          customFieldDefinitionId?: number;
          deleted?: boolean;
          purchaseOrderId?: number;
          syncTimestamp?: string;
          value?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "PurchaseOrderCustomFieldValue_customFieldDefinitionId_fkey";
            columns: ["customFieldDefinitionId"];
            isOneToOne: false;
            referencedRelation: "CustomFieldDefinition";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "PurchaseOrderCustomFieldValue_purchaseOrderId_fkey";
            columns: ["purchaseOrderId"];
            isOneToOne: false;
            referencedRelation: "PurchaseOrder";
            referencedColumns: ["id"];
          },
        ];
      };
      PurchaseOrderLineItem: {
        Row: {
          amount: number;
          classId: number | null;
          customerId: number | null;
          deleted: boolean;
          description: string | null;
          id: number;
          itemId: number;
          jobId: number | null;
          lineNumber: number;
          purchaseOrderId: number;
          quantity: number;
          syncTimestamp: string;
          taxable: boolean;
          taxCodeId: number | null;
          unitprice: number;
          uomId: number | null;
          vendorPartNumber: string | null;
          volume: number;
          volumeunit: string | null;
          weight: number;
          weightunit: string | null;
          workcenterId: number | null;
        };
        Insert: {
          amount: number;
          classId?: number | null;
          customerId?: number | null;
          deleted: boolean;
          description?: string | null;
          id: number;
          itemId: number;
          jobId?: number | null;
          lineNumber: number;
          purchaseOrderId: number;
          quantity: number;
          syncTimestamp: string;
          taxable: boolean;
          taxCodeId?: number | null;
          unitprice: number;
          uomId?: number | null;
          vendorPartNumber?: string | null;
          volume: number;
          volumeunit?: string | null;
          weight: number;
          weightunit?: string | null;
          workcenterId?: number | null;
        };
        Update: {
          amount?: number;
          classId?: number | null;
          customerId?: number | null;
          deleted?: boolean;
          description?: string | null;
          id?: number;
          itemId?: number;
          jobId?: number | null;
          lineNumber?: number;
          purchaseOrderId?: number;
          quantity?: number;
          syncTimestamp?: string;
          taxable?: boolean;
          taxCodeId?: number | null;
          unitprice?: number;
          uomId?: number | null;
          vendorPartNumber?: string | null;
          volume?: number;
          volumeunit?: string | null;
          weight?: number;
          weightunit?: string | null;
          workcenterId?: number | null;
        };
        Relationships: [
          {
            foreignKeyName: "PurchaseOrderLineItem_classId_fkey";
            columns: ["classId"];
            isOneToOne: false;
            referencedRelation: "Class";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "PurchaseOrderLineItem_customerId_fkey";
            columns: ["customerId"];
            isOneToOne: false;
            referencedRelation: "Customer";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "PurchaseOrderLineItem_itemId_fkey";
            columns: ["itemId"];
            isOneToOne: false;
            referencedRelation: "Item";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "PurchaseOrderLineItem_itemId_uomId_fkey";
            columns: ["itemId", "uomId"];
            isOneToOne: false;
            referencedRelation: "ItemUOM";
            referencedColumns: ["itemId", "uomId"];
          },
          {
            foreignKeyName: "PurchaseOrderLineItem_jobId_fkey";
            columns: ["jobId"];
            isOneToOne: false;
            referencedRelation: "Job";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "PurchaseOrderLineItem_purchaseOrderId_fkey";
            columns: ["purchaseOrderId"];
            isOneToOne: false;
            referencedRelation: "PurchaseOrder";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "PurchaseOrderLineItem_taxCodeId_fkey";
            columns: ["taxCodeId"];
            isOneToOne: false;
            referencedRelation: "TaxCode";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "PurchaseOrderLineItem_uomId_fkey";
            columns: ["uomId"];
            isOneToOne: false;
            referencedRelation: "UOM";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "PurchaseOrderLineItem_workcenterId_fkey";
            columns: ["workcenterId"];
            isOneToOne: false;
            referencedRelation: "Workcenter";
            referencedColumns: ["id"];
          },
        ];
      };
      PurchasingNote: {
        Row: {
          itemId: number;
          jobId: number;
          value: string;
          version: number;
        };
        Insert: {
          itemId: number;
          jobId: number;
          value: string;
          version?: number;
        };
        Update: {
          itemId?: number;
          jobId?: number;
          value?: string;
          version?: number;
        };
        Relationships: [
          {
            foreignKeyName: "PurchasingNote_itemId_fkey";
            columns: ["itemId"];
            isOneToOne: false;
            referencedRelation: "Item";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "PurchasingNote_jobId_fkey";
            columns: ["jobId"];
            isOneToOne: false;
            referencedRelation: "Job";
            referencedColumns: ["id"];
          },
        ];
      };
      ReceivingCost: {
        Row: {
          amount: number;
          bill: boolean;
          classId: number | null;
          deleted: boolean;
          id: number;
          itemId: number;
          itemReceiptId: number;
          lineNumber: number;
          syncTimestamp: string;
          vendorId: number | null;
        };
        Insert: {
          amount: number;
          bill: boolean;
          classId?: number | null;
          deleted: boolean;
          id: number;
          itemId: number;
          itemReceiptId: number;
          lineNumber: number;
          syncTimestamp: string;
          vendorId?: number | null;
        };
        Update: {
          amount?: number;
          bill?: boolean;
          classId?: number | null;
          deleted?: boolean;
          id?: number;
          itemId?: number;
          itemReceiptId?: number;
          lineNumber?: number;
          syncTimestamp?: string;
          vendorId?: number | null;
        };
        Relationships: [
          {
            foreignKeyName: "ReceivingCost_classId_fkey";
            columns: ["classId"];
            isOneToOne: false;
            referencedRelation: "Class";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "ReceivingCost_itemId_fkey";
            columns: ["itemId"];
            isOneToOne: false;
            referencedRelation: "Item";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "ReceivingCost_itemReceiptId_fkey";
            columns: ["itemReceiptId"];
            isOneToOne: false;
            referencedRelation: "ItemReceipt";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "ReceivingCost_vendorId_fkey";
            columns: ["vendorId"];
            isOneToOne: false;
            referencedRelation: "Vendor";
            referencedColumns: ["id"];
          },
        ];
      };
      SalesOrder: {
        Row: {
          archived: boolean;
          assignedToUserId: number | null;
          billing: SosTypes.TransactionAddress | null;
          channelId: number | null;
          closed: boolean;
          comment: string | null;
          currencyId: number;
          customerId: number;
          customerMessage: string | null;
          customerPO: string | null;
          date: string;
          deleted: boolean;
          departmentId: number | null;
          depositAmount: number;
          discountAmount: number;
          discountPercent: number;
          discountTaxable: boolean;
          dropShip: boolean;
          exchangeRate: number;
          id: number;
          locationId: number;
          number: string;
          orderStageId: number | null;
          priorityId: number | null;
          salesRepId: number | null;
          shipping: SosTypes.TransactionAddress | null;
          shippingAmount: number;
          shippingTaxable: boolean;
          starred: number;
          statusMessage: string | null;
          subTotal: number;
          syncTimestamp: string;
          syncToken: number;
          taxAmount: number;
          taxCodeId: number | null;
          taxPercent: number;
          termsId: number | null;
          total: number;
        };
        Insert: {
          archived: boolean;
          assignedToUserId?: number | null;
          billing?: SosTypes.TransactionAddress | null;
          channelId?: number | null;
          closed: boolean;
          comment?: string | null;
          currencyId: number;
          customerId: number;
          customerMessage?: string | null;
          customerPO?: string | null;
          date: string;
          deleted: boolean;
          departmentId?: number | null;
          depositAmount: number;
          discountAmount: number;
          discountPercent: number;
          discountTaxable: boolean;
          dropShip: boolean;
          exchangeRate: number;
          id: number;
          locationId: number;
          number: string;
          orderStageId?: number | null;
          priorityId?: number | null;
          salesRepId?: number | null;
          shipping?: SosTypes.TransactionAddress | null;
          shippingAmount: number;
          shippingTaxable: boolean;
          starred: number;
          statusMessage?: string | null;
          subTotal: number;
          syncTimestamp: string;
          syncToken: number;
          taxAmount: number;
          taxCodeId?: number | null;
          taxPercent: number;
          termsId?: number | null;
          total: number;
        };
        Update: {
          archived?: boolean;
          assignedToUserId?: number | null;
          billing?: SosTypes.TransactionAddress | null;
          channelId?: number | null;
          closed?: boolean;
          comment?: string | null;
          currencyId?: number;
          customerId?: number;
          customerMessage?: string | null;
          customerPO?: string | null;
          date?: string;
          deleted?: boolean;
          departmentId?: number | null;
          depositAmount?: number;
          discountAmount?: number;
          discountPercent?: number;
          discountTaxable?: boolean;
          dropShip?: boolean;
          exchangeRate?: number;
          id?: number;
          locationId?: number;
          number?: string;
          orderStageId?: number | null;
          priorityId?: number | null;
          salesRepId?: number | null;
          shipping?: SosTypes.TransactionAddress | null;
          shippingAmount?: number;
          shippingTaxable?: boolean;
          starred?: number;
          statusMessage?: string | null;
          subTotal?: number;
          syncTimestamp?: string;
          syncToken?: number;
          taxAmount?: number;
          taxCodeId?: number | null;
          taxPercent?: number;
          termsId?: number | null;
          total?: number;
        };
        Relationships: [
          {
            foreignKeyName: "SalesOrder_assignedToUserId_fkey";
            columns: ["assignedToUserId"];
            isOneToOne: false;
            referencedRelation: "User";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "SalesOrder_channelId_fkey";
            columns: ["channelId"];
            isOneToOne: false;
            referencedRelation: "Channel";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "SalesOrder_currencyId_fkey";
            columns: ["currencyId"];
            isOneToOne: false;
            referencedRelation: "Currency";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "SalesOrder_customerId_fkey";
            columns: ["customerId"];
            isOneToOne: false;
            referencedRelation: "Customer";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "SalesOrder_departmentId_fkey";
            columns: ["departmentId"];
            isOneToOne: false;
            referencedRelation: "Department";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "SalesOrder_locationId_fkey";
            columns: ["locationId"];
            isOneToOne: false;
            referencedRelation: "Location";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "SalesOrder_orderStageId_fkey";
            columns: ["orderStageId"];
            isOneToOne: false;
            referencedRelation: "OrderStage";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "SalesOrder_priorityId_fkey";
            columns: ["priorityId"];
            isOneToOne: false;
            referencedRelation: "Priority";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "SalesOrder_salesRepId_fkey";
            columns: ["salesRepId"];
            isOneToOne: false;
            referencedRelation: "SalesRep";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "SalesOrder_taxCodeId_fkey";
            columns: ["taxCodeId"];
            isOneToOne: false;
            referencedRelation: "TaxCode";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "SalesOrder_termsId_fkey";
            columns: ["termsId"];
            isOneToOne: false;
            referencedRelation: "Terms";
            referencedColumns: ["id"];
          },
        ];
      };
      SalesOrderCustomFieldValue: {
        Row: {
          customFieldDefinitionId: number;
          deleted: boolean;
          salesOrderId: number;
          syncTimestamp: string;
          value: string | null;
        };
        Insert: {
          customFieldDefinitionId: number;
          deleted: boolean;
          salesOrderId: number;
          syncTimestamp: string;
          value?: string | null;
        };
        Update: {
          customFieldDefinitionId?: number;
          deleted?: boolean;
          salesOrderId?: number;
          syncTimestamp?: string;
          value?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "SalesOrderCustomFieldValue_customFieldDefinitionId_fkey";
            columns: ["customFieldDefinitionId"];
            isOneToOne: false;
            referencedRelation: "CustomFieldDefinition";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "SalesOrderCustomFieldValue_salesOrderId_fkey";
            columns: ["salesOrderId"];
            isOneToOne: false;
            referencedRelation: "SalesOrder";
            referencedColumns: ["id"];
          },
        ];
      };
      SalesOrderLineItem: {
        Row: {
          amount: number;
          classId: number | null;
          cost: number | null;
          deleted: boolean;
          description: string | null;
          duedate: string | null;
          id: number;
          invoiced: number;
          itemId: number;
          jobId: number | null;
          lineNumber: number;
          listPrice: number | null;
          margin: number | null;
          percentdiscount: number | null;
          picked: number;
          quantity: number;
          salesOrderId: number;
          shipped: number;
          syncTimestamp: string;
          taxable: boolean;
          taxCodeId: number | null;
          unitprice: number;
          uomId: number | null;
          volume: number;
          volumeunit: string | null;
          weight: number;
          weightunit: string | null;
          workcenterId: number | null;
        };
        Insert: {
          amount: number;
          classId?: number | null;
          cost?: number | null;
          deleted: boolean;
          description?: string | null;
          duedate?: string | null;
          id: number;
          invoiced: number;
          itemId: number;
          jobId?: number | null;
          lineNumber: number;
          listPrice?: number | null;
          margin?: number | null;
          percentdiscount?: number | null;
          picked: number;
          quantity: number;
          salesOrderId: number;
          shipped: number;
          syncTimestamp: string;
          taxable: boolean;
          taxCodeId?: number | null;
          unitprice: number;
          uomId?: number | null;
          volume: number;
          volumeunit?: string | null;
          weight: number;
          weightunit?: string | null;
          workcenterId?: number | null;
        };
        Update: {
          amount?: number;
          classId?: number | null;
          cost?: number | null;
          deleted?: boolean;
          description?: string | null;
          duedate?: string | null;
          id?: number;
          invoiced?: number;
          itemId?: number;
          jobId?: number | null;
          lineNumber?: number;
          listPrice?: number | null;
          margin?: number | null;
          percentdiscount?: number | null;
          picked?: number;
          quantity?: number;
          salesOrderId?: number;
          shipped?: number;
          syncTimestamp?: string;
          taxable?: boolean;
          taxCodeId?: number | null;
          unitprice?: number;
          uomId?: number | null;
          volume?: number;
          volumeunit?: string | null;
          weight?: number;
          weightunit?: string | null;
          workcenterId?: number | null;
        };
        Relationships: [
          {
            foreignKeyName: "SalesOrderLineItem_classId_fkey";
            columns: ["classId"];
            isOneToOne: false;
            referencedRelation: "Class";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "SalesOrderLineItem_itemId_fkey";
            columns: ["itemId"];
            isOneToOne: false;
            referencedRelation: "Item";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "SalesOrderLineItem_itemId_uomId_fkey";
            columns: ["itemId", "uomId"];
            isOneToOne: false;
            referencedRelation: "ItemUOM";
            referencedColumns: ["itemId", "uomId"];
          },
          {
            foreignKeyName: "SalesOrderLineItem_jobId_fkey";
            columns: ["jobId"];
            isOneToOne: false;
            referencedRelation: "Job";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "SalesOrderLineItem_salesOrderId_fkey";
            columns: ["salesOrderId"];
            isOneToOne: false;
            referencedRelation: "SalesOrder";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "SalesOrderLineItem_taxCodeId_fkey";
            columns: ["taxCodeId"];
            isOneToOne: false;
            referencedRelation: "TaxCode";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "SalesOrderLineItem_uomId_fkey";
            columns: ["uomId"];
            isOneToOne: false;
            referencedRelation: "UOM";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "SalesOrderLineItem_workcenterId_fkey";
            columns: ["workcenterId"];
            isOneToOne: false;
            referencedRelation: "Workcenter";
            referencedColumns: ["id"];
          },
        ];
      };
      SalesRep: {
        Row: {
          deleted: boolean;
          firstName: string;
          id: number;
          lastName: string;
          syncTimestamp: string;
          syncToken: number;
        };
        Insert: {
          deleted: boolean;
          firstName: string;
          id: number;
          lastName: string;
          syncTimestamp: string;
          syncToken: number;
        };
        Update: {
          deleted?: boolean;
          firstName?: string;
          id?: number;
          lastName?: string;
          syncTimestamp?: string;
          syncToken?: number;
        };
        Relationships: [];
      };
      Shipment: {
        Row: {
          archived: boolean;
          assignedToUserId: number | null;
          billing: SosTypes.TransactionAddress | null;
          channelId: number | null;
          comment: string | null;
          customerId: number;
          customerMessage: string | null;
          customerPO: string | null;
          date: string;
          deleted: boolean;
          departmentId: number | null;
          id: number;
          locationId: number;
          number: string;
          priorityId: number | null;
          shipBy: string | null;
          shipping: SosTypes.TransactionAddress | null;
          shippingAmount: number;
          shippingMethodId: number | null;
          starred: number;
          syncTimestamp: string;
          syncToken: number;
          trackingLink: string;
          trackingNumber: string | null;
        };
        Insert: {
          archived: boolean;
          assignedToUserId?: number | null;
          billing?: SosTypes.TransactionAddress | null;
          channelId?: number | null;
          comment?: string | null;
          customerId: number;
          customerMessage?: string | null;
          customerPO?: string | null;
          date: string;
          deleted: boolean;
          departmentId?: number | null;
          id: number;
          locationId: number;
          number: string;
          priorityId?: number | null;
          shipBy?: string | null;
          shipping?: SosTypes.TransactionAddress | null;
          shippingAmount: number;
          shippingMethodId?: number | null;
          starred: number;
          syncTimestamp: string;
          syncToken: number;
          trackingLink: string;
          trackingNumber?: string | null;
        };
        Update: {
          archived?: boolean;
          assignedToUserId?: number | null;
          billing?: SosTypes.TransactionAddress | null;
          channelId?: number | null;
          comment?: string | null;
          customerId?: number;
          customerMessage?: string | null;
          customerPO?: string | null;
          date?: string;
          deleted?: boolean;
          departmentId?: number | null;
          id?: number;
          locationId?: number;
          number?: string;
          priorityId?: number | null;
          shipBy?: string | null;
          shipping?: SosTypes.TransactionAddress | null;
          shippingAmount?: number;
          shippingMethodId?: number | null;
          starred?: number;
          syncTimestamp?: string;
          syncToken?: number;
          trackingLink?: string;
          trackingNumber?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "Shipment_assignedToUserId_fkey";
            columns: ["assignedToUserId"];
            isOneToOne: false;
            referencedRelation: "User";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "Shipment_channelId_fkey";
            columns: ["channelId"];
            isOneToOne: false;
            referencedRelation: "Channel";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "Shipment_customerId_fkey";
            columns: ["customerId"];
            isOneToOne: false;
            referencedRelation: "Customer";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "Shipment_departmentId_fkey";
            columns: ["departmentId"];
            isOneToOne: false;
            referencedRelation: "Department";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "Shipment_locationId_fkey";
            columns: ["locationId"];
            isOneToOne: false;
            referencedRelation: "Location";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "Shipment_priorityId_fkey";
            columns: ["priorityId"];
            isOneToOne: false;
            referencedRelation: "Priority";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "Shipment_shippingMethodId_fkey";
            columns: ["shippingMethodId"];
            isOneToOne: false;
            referencedRelation: "ShipMethod";
            referencedColumns: ["id"];
          },
        ];
      };
      ShipmentCustomFieldValue: {
        Row: {
          customFieldDefinitionId: number;
          deleted: boolean;
          shipmentId: number;
          syncTimestamp: string;
          value: string | null;
        };
        Insert: {
          customFieldDefinitionId: number;
          deleted: boolean;
          shipmentId: number;
          syncTimestamp: string;
          value?: string | null;
        };
        Update: {
          customFieldDefinitionId?: number;
          deleted?: boolean;
          shipmentId?: number;
          syncTimestamp?: string;
          value?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "ShipmentCustomFieldValue_customFieldDefinitionId_fkey";
            columns: ["customFieldDefinitionId"];
            isOneToOne: false;
            referencedRelation: "CustomFieldDefinition";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "ShipmentCustomFieldValue_shipmentId_fkey";
            columns: ["shipmentId"];
            isOneToOne: false;
            referencedRelation: "Shipment";
            referencedColumns: ["id"];
          },
        ];
      };
      ShipmentLineItem: {
        Row: {
          binId: number | null;
          classId: number | null;
          deleted: boolean;
          description: string | null;
          id: number;
          invoiced: number;
          itemId: number;
          jobId: number | null;
          lineNumber: number;
          linkedInvoiceLineItemId: number | null;
          linkedSalesOrderLineItemId: number | null;
          linkedSalesReceiptLineItemId: number | null;
          lotId: number | null;
          quantity: number;
          serials: SosTypes.Reference[] | null;
          shipmentId: number;
          syncTimestamp: string;
          uomId: number | null;
          volume: number;
          volumeunit: string | null;
          weight: number;
          weightunit: string | null;
          workcenterId: number | null;
        };
        Insert: {
          binId?: number | null;
          classId?: number | null;
          deleted: boolean;
          description?: string | null;
          id: number;
          invoiced: number;
          itemId: number;
          jobId?: number | null;
          lineNumber: number;
          linkedInvoiceLineItemId?: number | null;
          linkedSalesOrderLineItemId?: number | null;
          linkedSalesReceiptLineItemId?: number | null;
          lotId?: number | null;
          quantity: number;
          serials?: SosTypes.Reference[] | null;
          shipmentId: number;
          syncTimestamp: string;
          uomId?: number | null;
          volume: number;
          volumeunit?: string | null;
          weight: number;
          weightunit?: string | null;
          workcenterId?: number | null;
        };
        Update: {
          binId?: number | null;
          classId?: number | null;
          deleted?: boolean;
          description?: string | null;
          id?: number;
          invoiced?: number;
          itemId?: number;
          jobId?: number | null;
          lineNumber?: number;
          linkedInvoiceLineItemId?: number | null;
          linkedSalesOrderLineItemId?: number | null;
          linkedSalesReceiptLineItemId?: number | null;
          lotId?: number | null;
          quantity?: number;
          serials?: SosTypes.Reference[] | null;
          shipmentId?: number;
          syncTimestamp?: string;
          uomId?: number | null;
          volume?: number;
          volumeunit?: string | null;
          weight?: number;
          weightunit?: string | null;
          workcenterId?: number | null;
        };
        Relationships: [
          {
            foreignKeyName: "ShipmentLineItem_binId_fkey";
            columns: ["binId"];
            isOneToOne: false;
            referencedRelation: "Bin";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "ShipmentLineItem_classId_fkey";
            columns: ["classId"];
            isOneToOne: false;
            referencedRelation: "Class";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "ShipmentLineItem_itemId_fkey";
            columns: ["itemId"];
            isOneToOne: false;
            referencedRelation: "Item";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "ShipmentLineItem_itemId_uomId_fkey";
            columns: ["itemId", "uomId"];
            isOneToOne: false;
            referencedRelation: "ItemUOM";
            referencedColumns: ["itemId", "uomId"];
          },
          {
            foreignKeyName: "ShipmentLineItem_jobId_fkey";
            columns: ["jobId"];
            isOneToOne: false;
            referencedRelation: "Job";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "ShipmentLineItem_linkedSalesOrderLineItemId_fkey";
            columns: ["linkedSalesOrderLineItemId"];
            isOneToOne: false;
            referencedRelation: "SalesOrderLineItem";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "ShipmentLineItem_shipmentId_fkey";
            columns: ["shipmentId"];
            isOneToOne: false;
            referencedRelation: "Shipment";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "ShipmentLineItem_uomId_fkey";
            columns: ["uomId"];
            isOneToOne: false;
            referencedRelation: "UOM";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "ShipmentLineItem_workcenterId_fkey";
            columns: ["workcenterId"];
            isOneToOne: false;
            referencedRelation: "Workcenter";
            referencedColumns: ["id"];
          },
        ];
      };
      ShipMethod: {
        Row: {
          deleted: boolean;
          id: number;
          name: string;
          syncTimestamp: string;
          syncToken: number;
        };
        Insert: {
          deleted: boolean;
          id: number;
          name: string;
          syncTimestamp: string;
          syncToken: number;
        };
        Update: {
          deleted?: boolean;
          id?: number;
          name?: string;
          syncTimestamp?: string;
          syncToken?: number;
        };
        Relationships: [];
      };
      SyncMetadata: {
        Row: {
          entityType: string;
          lastUpdated: string;
        };
        Insert: {
          entityType: string;
          lastUpdated: string;
        };
        Update: {
          entityType?: string;
          lastUpdated?: string;
        };
        Relationships: [];
      };
      TaxCode: {
        Row: {
          deleted: boolean;
          id: number;
          isPurchaseTaxType: boolean;
          isSalesTaxType: boolean;
          name: string;
          purchaseTaxRate: number;
          salesTaxRate: number;
          syncTimestamp: string;
          syncToken: number;
        };
        Insert: {
          deleted: boolean;
          id: number;
          isPurchaseTaxType: boolean;
          isSalesTaxType: boolean;
          name: string;
          purchaseTaxRate: number;
          salesTaxRate: number;
          syncTimestamp: string;
          syncToken: number;
        };
        Update: {
          deleted?: boolean;
          id?: number;
          isPurchaseTaxType?: boolean;
          isSalesTaxType?: boolean;
          name?: string;
          purchaseTaxRate?: number;
          salesTaxRate?: number;
          syncTimestamp?: string;
          syncToken?: number;
        };
        Relationships: [];
      };
      Terms: {
        Row: {
          deleted: boolean;
          id: number;
          name: string;
          syncTimestamp: string;
          syncToken: number;
        };
        Insert: {
          deleted: boolean;
          id: number;
          name: string;
          syncTimestamp: string;
          syncToken: number;
        };
        Update: {
          deleted?: boolean;
          id?: number;
          name?: string;
          syncTimestamp?: string;
          syncToken?: number;
        };
        Relationships: [];
      };
      Transfer: {
        Row: {
          archived: boolean;
          comment: string | null;
          createBillForShippingAmount: boolean | null;
          date: string | null;
          deleted: boolean;
          departmentId: number | null;
          fromLocationId: number;
          id: number;
          number: string;
          shippingAmount: number;
          shippingMethodId: number | null;
          starred: number;
          syncTimestamp: string;
          syncToken: number;
          toLocationId: number;
          trackingNumber: string | null;
        };
        Insert: {
          archived: boolean;
          comment?: string | null;
          createBillForShippingAmount?: boolean | null;
          date?: string | null;
          deleted: boolean;
          departmentId?: number | null;
          fromLocationId: number;
          id: number;
          number: string;
          shippingAmount: number;
          shippingMethodId?: number | null;
          starred: number;
          syncTimestamp: string;
          syncToken: number;
          toLocationId: number;
          trackingNumber?: string | null;
        };
        Update: {
          archived?: boolean;
          comment?: string | null;
          createBillForShippingAmount?: boolean | null;
          date?: string | null;
          deleted?: boolean;
          departmentId?: number | null;
          fromLocationId?: number;
          id?: number;
          number?: string;
          shippingAmount?: number;
          shippingMethodId?: number | null;
          starred?: number;
          syncTimestamp?: string;
          syncToken?: number;
          toLocationId?: number;
          trackingNumber?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "Transfer_departmentId_fkey";
            columns: ["departmentId"];
            isOneToOne: false;
            referencedRelation: "Department";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "Transfer_fromLocationId_fkey";
            columns: ["fromLocationId"];
            isOneToOne: false;
            referencedRelation: "Location";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "Transfer_shippingMethodId_fkey";
            columns: ["shippingMethodId"];
            isOneToOne: false;
            referencedRelation: "ShipMethod";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "Transfer_toLocationId_fkey";
            columns: ["toLocationId"];
            isOneToOne: false;
            referencedRelation: "Location";
            referencedColumns: ["id"];
          },
        ];
      };
      TransferLineItem: {
        Row: {
          classId: number | null;
          deleted: boolean;
          description: string | null;
          fromBinId: number | null;
          id: number;
          itemId: number;
          jobId: number | null;
          lineNumber: number;
          lotId: number | null;
          quantity: number;
          serials: SosTypes.Reference[] | null;
          syncTimestamp: string;
          toBinId: number | null;
          transferId: number;
          uomId: number | null;
          volume: number;
          volumeunit: string | null;
          weight: number;
          weightunit: string | null;
          workcenterId: number | null;
        };
        Insert: {
          classId?: number | null;
          deleted: boolean;
          description?: string | null;
          fromBinId?: number | null;
          id: number;
          itemId: number;
          jobId?: number | null;
          lineNumber: number;
          lotId?: number | null;
          quantity: number;
          serials?: SosTypes.Reference[] | null;
          syncTimestamp: string;
          toBinId?: number | null;
          transferId: number;
          uomId?: number | null;
          volume: number;
          volumeunit?: string | null;
          weight: number;
          weightunit?: string | null;
          workcenterId?: number | null;
        };
        Update: {
          classId?: number | null;
          deleted?: boolean;
          description?: string | null;
          fromBinId?: number | null;
          id?: number;
          itemId?: number;
          jobId?: number | null;
          lineNumber?: number;
          lotId?: number | null;
          quantity?: number;
          serials?: SosTypes.Reference[] | null;
          syncTimestamp?: string;
          toBinId?: number | null;
          transferId?: number;
          uomId?: number | null;
          volume?: number;
          volumeunit?: string | null;
          weight?: number;
          weightunit?: string | null;
          workcenterId?: number | null;
        };
        Relationships: [
          {
            foreignKeyName: "TransferLineItem_classId_fkey";
            columns: ["classId"];
            isOneToOne: false;
            referencedRelation: "Class";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "TransferLineItem_fromBinId_fkey";
            columns: ["fromBinId"];
            isOneToOne: false;
            referencedRelation: "Bin";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "TransferLineItem_itemId_fkey";
            columns: ["itemId"];
            isOneToOne: false;
            referencedRelation: "Item";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "TransferLineItem_itemId_uomId_fkey";
            columns: ["itemId", "uomId"];
            isOneToOne: false;
            referencedRelation: "ItemUOM";
            referencedColumns: ["itemId", "uomId"];
          },
          {
            foreignKeyName: "TransferLineItem_jobId_fkey";
            columns: ["jobId"];
            isOneToOne: false;
            referencedRelation: "Job";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "TransferLineItem_toBinId_fkey";
            columns: ["toBinId"];
            isOneToOne: false;
            referencedRelation: "Bin";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "TransferLineItem_transferId_fkey";
            columns: ["transferId"];
            isOneToOne: false;
            referencedRelation: "Transfer";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "TransferLineItem_uomId_fkey";
            columns: ["uomId"];
            isOneToOne: false;
            referencedRelation: "UOM";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "TransferLineItem_workcenterId_fkey";
            columns: ["workcenterId"];
            isOneToOne: false;
            referencedRelation: "Workcenter";
            referencedColumns: ["id"];
          },
        ];
      };
      UOM: {
        Row: {
          abbreviation: string;
          deleted: boolean;
          id: number;
          name: string;
          syncTimestamp: string;
          syncToken: number;
        };
        Insert: {
          abbreviation: string;
          deleted: boolean;
          id: number;
          name: string;
          syncTimestamp: string;
          syncToken: number;
        };
        Update: {
          abbreviation?: string;
          deleted?: boolean;
          id?: number;
          name?: string;
          syncTimestamp?: string;
          syncToken?: number;
        };
        Relationships: [];
      };
      User: {
        Row: {
          active: boolean;
          admin: boolean;
          deleted: boolean;
          email: string;
          id: number;
          lastLogin: string | null;
          locked: boolean;
          master: boolean;
          name: string;
          syncTimestamp: string;
          syncToken: number;
        };
        Insert: {
          active: boolean;
          admin: boolean;
          deleted: boolean;
          email: string;
          id: number;
          lastLogin?: string | null;
          locked: boolean;
          master: boolean;
          name: string;
          syncTimestamp: string;
          syncToken: number;
        };
        Update: {
          active?: boolean;
          admin?: boolean;
          deleted?: boolean;
          email?: string;
          id?: number;
          lastLogin?: string | null;
          locked?: boolean;
          master?: boolean;
          name?: string;
          syncTimestamp?: string;
          syncToken?: number;
        };
        Relationships: [];
      };
      UserGoogleToken: {
        Row: {
          createdAt: string;
          encryptedAccessToken: string;
          encryptedRefreshToken: string;
          expiresAt: string;
          scopes: string;
          updatedAt: string;
          userId: string;
        };
        Insert: {
          createdAt?: string;
          encryptedAccessToken: string;
          encryptedRefreshToken: string;
          expiresAt: string;
          scopes: string;
          updatedAt?: string;
          userId: string;
        };
        Update: {
          createdAt?: string;
          encryptedAccessToken?: string;
          encryptedRefreshToken?: string;
          expiresAt?: string;
          scopes?: string;
          updatedAt?: string;
          userId?: string;
        };
        Relationships: [];
      };
      Vendor: {
        Row: {
          accountNumber: string | null;
          address: SosTypes.Address | null;
          altAddresses: SosTypes.Address[] | null;
          altPhone: string | null;
          archived: boolean;
          companyName: string | null;
          contact: SosTypes.Contact | null;
          currencyId: number | null;
          deleted: boolean;
          email: string | null;
          fax: string | null;
          id: number;
          mobile: string | null;
          name: string;
          notes: string | null;
          phone: string | null;
          showOnForms: boolean;
          starred: number;
          syncTimestamp: string;
          syncToken: number;
          taxCodeId: number | null;
          termsId: number | null;
          website: string | null;
        };
        Insert: {
          accountNumber?: string | null;
          address?: SosTypes.Address | null;
          altAddresses?: SosTypes.Address[] | null;
          altPhone?: string | null;
          archived: boolean;
          companyName?: string | null;
          contact?: SosTypes.Contact | null;
          currencyId?: number | null;
          deleted: boolean;
          email?: string | null;
          fax?: string | null;
          id: number;
          mobile?: string | null;
          name: string;
          notes?: string | null;
          phone?: string | null;
          showOnForms: boolean;
          starred: number;
          syncTimestamp: string;
          syncToken: number;
          taxCodeId?: number | null;
          termsId?: number | null;
          website?: string | null;
        };
        Update: {
          accountNumber?: string | null;
          address?: SosTypes.Address | null;
          altAddresses?: SosTypes.Address[] | null;
          altPhone?: string | null;
          archived?: boolean;
          companyName?: string | null;
          contact?: SosTypes.Contact | null;
          currencyId?: number | null;
          deleted?: boolean;
          email?: string | null;
          fax?: string | null;
          id?: number;
          mobile?: string | null;
          name?: string;
          notes?: string | null;
          phone?: string | null;
          showOnForms?: boolean;
          starred?: number;
          syncTimestamp?: string;
          syncToken?: number;
          taxCodeId?: number | null;
          termsId?: number | null;
          website?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "Vendor_currencyId_fkey";
            columns: ["currencyId"];
            isOneToOne: false;
            referencedRelation: "Currency";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "Vendor_taxCodeId_fkey";
            columns: ["taxCodeId"];
            isOneToOne: false;
            referencedRelation: "TaxCode";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "Vendor_termsId_fkey";
            columns: ["termsId"];
            isOneToOne: false;
            referencedRelation: "Terms";
            referencedColumns: ["id"];
          },
        ];
      };
      VendorCustomFieldValue: {
        Row: {
          customFieldDefinitionId: number;
          deleted: boolean;
          syncTimestamp: string;
          value: string | null;
          vendorId: number;
        };
        Insert: {
          customFieldDefinitionId: number;
          deleted: boolean;
          syncTimestamp: string;
          value?: string | null;
          vendorId: number;
        };
        Update: {
          customFieldDefinitionId?: number;
          deleted?: boolean;
          syncTimestamp?: string;
          value?: string | null;
          vendorId?: number;
        };
        Relationships: [
          {
            foreignKeyName: "VendorCustomFieldValue_customFieldDefinitionId_fkey";
            columns: ["customFieldDefinitionId"];
            isOneToOne: false;
            referencedRelation: "CustomFieldDefinition";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "VendorCustomFieldValue_vendorId_fkey";
            columns: ["vendorId"];
            isOneToOne: false;
            referencedRelation: "Vendor";
            referencedColumns: ["id"];
          },
        ];
      };
      VendorItemCatalog: {
        Row: {
          itemId: number;
          price: number;
          vendorId: number;
          vendorPartNumber: string | null;
        };
        Insert: {
          itemId: number;
          price: number;
          vendorId: number;
          vendorPartNumber?: string | null;
        };
        Update: {
          itemId?: number;
          price?: number;
          vendorId?: number;
          vendorPartNumber?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "VendorItemCatalog_itemId_fkey";
            columns: ["itemId"];
            isOneToOne: false;
            referencedRelation: "Item";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "VendorItemCatalog_vendorId_fkey";
            columns: ["vendorId"];
            isOneToOne: false;
            referencedRelation: "Vendor";
            referencedColumns: ["id"];
          },
        ];
      };
      Warranty: {
        Row: {
          deleted: boolean;
          id: number;
          name: string;
          syncTimestamp: string;
          syncToken: number;
        };
        Insert: {
          deleted: boolean;
          id: number;
          name: string;
          syncTimestamp: string;
          syncToken: number;
        };
        Update: {
          deleted?: boolean;
          id?: number;
          name?: string;
          syncTimestamp?: string;
          syncToken?: number;
        };
        Relationships: [];
      };
      Workcenter: {
        Row: {
          assetAccountId: number | null;
          deleted: boolean;
          id: number;
          name: string;
          sortOrder: number;
          syncTimestamp: string;
          syncToken: number;
        };
        Insert: {
          assetAccountId?: number | null;
          deleted: boolean;
          id: number;
          name: string;
          sortOrder: number;
          syncTimestamp: string;
          syncToken: number;
        };
        Update: {
          assetAccountId?: number | null;
          deleted?: boolean;
          id?: number;
          name?: string;
          sortOrder?: number;
          syncTimestamp?: string;
          syncToken?: number;
        };
        Relationships: [
          {
            foreignKeyName: "fk_asset_account";
            columns: ["assetAccountId"];
            isOneToOne: false;
            referencedRelation: "Account";
            referencedColumns: ["id"];
          },
        ];
      };
      Worker: {
        Row: {
          deleted: boolean;
          firstName: string;
          id: number;
          lastName: string;
          syncTimestamp: string;
          syncToken: number;
        };
        Insert: {
          deleted: boolean;
          firstName: string;
          id: number;
          lastName: string;
          syncTimestamp: string;
          syncToken: number;
        };
        Update: {
          deleted?: boolean;
          firstName?: string;
          id?: number;
          lastName?: string;
          syncTimestamp?: string;
          syncToken?: number;
        };
        Relationships: [];
      };
      WorkOrder: {
        Row: {
          archived: boolean;
          assignedToUserId: number | null;
          channelId: number | null;
          closed: boolean;
          comment: string | null;
          customerId: number | null;
          date: string;
          deleted: boolean;
          departmentId: number | null;
          id: number;
          locationId: number;
          number: string;
          priorityId: number | null;
          starred: number;
          syncTimestamp: string;
          syncToken: number;
          total: number;
        };
        Insert: {
          archived: boolean;
          assignedToUserId?: number | null;
          channelId?: number | null;
          closed: boolean;
          comment?: string | null;
          customerId?: number | null;
          date: string;
          deleted: boolean;
          departmentId?: number | null;
          id: number;
          locationId: number;
          number: string;
          priorityId?: number | null;
          starred: number;
          syncTimestamp: string;
          syncToken: number;
          total: number;
        };
        Update: {
          archived?: boolean;
          assignedToUserId?: number | null;
          channelId?: number | null;
          closed?: boolean;
          comment?: string | null;
          customerId?: number | null;
          date?: string;
          deleted?: boolean;
          departmentId?: number | null;
          id?: number;
          locationId?: number;
          number?: string;
          priorityId?: number | null;
          starred?: number;
          syncTimestamp?: string;
          syncToken?: number;
          total?: number;
        };
        Relationships: [
          {
            foreignKeyName: "WorkOrder_assignedToUserId_fkey";
            columns: ["assignedToUserId"];
            isOneToOne: false;
            referencedRelation: "User";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "WorkOrder_channelId_fkey";
            columns: ["channelId"];
            isOneToOne: false;
            referencedRelation: "Channel";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "WorkOrder_customerId_fkey";
            columns: ["customerId"];
            isOneToOne: false;
            referencedRelation: "Customer";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "WorkOrder_departmentId_fkey";
            columns: ["departmentId"];
            isOneToOne: false;
            referencedRelation: "Department";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "WorkOrder_locationId_fkey";
            columns: ["locationId"];
            isOneToOne: false;
            referencedRelation: "Location";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "WorkOrder_priorityId_fkey";
            columns: ["priorityId"];
            isOneToOne: false;
            referencedRelation: "Priority";
            referencedColumns: ["id"];
          },
        ];
      };
      WorkOrderCustomFieldValue: {
        Row: {
          customFieldDefinitionId: number;
          deleted: boolean;
          syncTimestamp: string;
          value: string | null;
          workOrderId: number;
        };
        Insert: {
          customFieldDefinitionId: number;
          deleted: boolean;
          syncTimestamp: string;
          value?: string | null;
          workOrderId: number;
        };
        Update: {
          customFieldDefinitionId?: number;
          deleted?: boolean;
          syncTimestamp?: string;
          value?: string | null;
          workOrderId?: number;
        };
        Relationships: [
          {
            foreignKeyName: "WorkOrderCustomFieldValue_customFieldDefinitionId_fkey";
            columns: ["customFieldDefinitionId"];
            isOneToOne: false;
            referencedRelation: "CustomFieldDefinition";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "WorkOrderCustomFieldValue_workOrderId_fkey";
            columns: ["workOrderId"];
            isOneToOne: false;
            referencedRelation: "WorkOrder";
            referencedColumns: ["id"];
          },
        ];
      };
      WorkOrderLineItem: {
        Row: {
          classId: number | null;
          deleted: boolean;
          description: string | null;
          duedate: string | null;
          id: number;
          itemId: number;
          jobId: number | null;
          lineNumber: number;
          linkedItemReceiptLineItemId: number | null;
          linkedSalesOrderLineItemId: number | null;
          linkedSalesReceiptLineItemId: number | null;
          produced: number;
          quantity: number;
          syncTimestamp: string;
          unitprice: number;
          uomId: number | null;
          volume: number;
          volumeunit: string | null;
          weight: number;
          weightunit: string | null;
          workcenterId: number | null;
          workOrderId: number;
        };
        Insert: {
          classId?: number | null;
          deleted: boolean;
          description?: string | null;
          duedate?: string | null;
          id: number;
          itemId: number;
          jobId?: number | null;
          lineNumber: number;
          linkedItemReceiptLineItemId?: number | null;
          linkedSalesOrderLineItemId?: number | null;
          linkedSalesReceiptLineItemId?: number | null;
          produced: number;
          quantity: number;
          syncTimestamp: string;
          unitprice: number;
          uomId?: number | null;
          volume: number;
          volumeunit?: string | null;
          weight: number;
          weightunit?: string | null;
          workcenterId?: number | null;
          workOrderId: number;
        };
        Update: {
          classId?: number | null;
          deleted?: boolean;
          description?: string | null;
          duedate?: string | null;
          id?: number;
          itemId?: number;
          jobId?: number | null;
          lineNumber?: number;
          linkedItemReceiptLineItemId?: number | null;
          linkedSalesOrderLineItemId?: number | null;
          linkedSalesReceiptLineItemId?: number | null;
          produced?: number;
          quantity?: number;
          syncTimestamp?: string;
          unitprice?: number;
          uomId?: number | null;
          volume?: number;
          volumeunit?: string | null;
          weight?: number;
          weightunit?: string | null;
          workcenterId?: number | null;
          workOrderId?: number;
        };
        Relationships: [
          {
            foreignKeyName: "WorkOrderLineItem_classId_fkey";
            columns: ["classId"];
            isOneToOne: false;
            referencedRelation: "Class";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "WorkOrderLineItem_itemId_fkey";
            columns: ["itemId"];
            isOneToOne: false;
            referencedRelation: "Item";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "WorkOrderLineItem_itemId_uomId_fkey";
            columns: ["itemId", "uomId"];
            isOneToOne: false;
            referencedRelation: "ItemUOM";
            referencedColumns: ["itemId", "uomId"];
          },
          {
            foreignKeyName: "WorkOrderLineItem_jobId_fkey";
            columns: ["jobId"];
            isOneToOne: false;
            referencedRelation: "Job";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "WorkOrderLineItem_linkedItemReceiptLineItemId_fkey";
            columns: ["linkedItemReceiptLineItemId"];
            isOneToOne: false;
            referencedRelation: "ItemReceiptLineItem";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "WorkOrderLineItem_linkedSalesOrderLineItemId_fkey";
            columns: ["linkedSalesOrderLineItemId"];
            isOneToOne: false;
            referencedRelation: "SalesOrderLineItem";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "WorkOrderLineItem_uomId_fkey";
            columns: ["uomId"];
            isOneToOne: false;
            referencedRelation: "UOM";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "WorkOrderLineItem_workcenterId_fkey";
            columns: ["workcenterId"];
            isOneToOne: false;
            referencedRelation: "Workcenter";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "WorkOrderLineItem_workOrderId_fkey";
            columns: ["workOrderId"];
            isOneToOne: false;
            referencedRelation: "WorkOrder";
            referencedColumns: ["id"];
          },
        ];
      };
    };
    Views: {
      [_ in never]: never;
    };
    Functions: {
      getExpandedBOMHierarchy: {
        Args: {
          assemblyIds: number[];
          depthFirst?: boolean;
          pageLimit?: number;
          pageOffset?: number;
        };
        Returns: {
          data: {
            notes: string;
            assemblyId: number;
            lineNumber: number;
            quantity: number;
            componentId: number;
            depth: number;
            path: number[];
          }[];
          count: number;
        };
      };
      getExpandedBOMRecords: {
        Args: {
          assemblyIds: number[];
        };
        Returns: {
          assemblyId: number;
          lineNumber: number;
          componentId: number;
          quantity: number;
          notes: string;
        }[];
      };
    };
    Enums: {
      CustomFieldDataType: "Text" | "Boolean" | "List" | "Number" | "TextArea" | "Date" | "Money";
      EstimateStatus: "Accepted" | "Pending" | "Closed" | "Rejected";
      ItemType:
        | "Inventory Item"
        | "Non-inventory Item"
        | "Category"
        | "Expense"
        | "Assembly"
        | "Item Group"
        | "Service"
        | "Labor"
        | "Overhead"
        | "Other";
      LongRunningGasRequestStatus: "pending" | "processing" | "completed";
      ReceivedStatus: "" | "All" | "Some";
    };
    CompositeTypes: {
      [_ in never]: never;
    };
  };
};

type DatabaseWithoutInternals = Omit<Database, "__InternalSupabase">;

type DefaultSchema = DatabaseWithoutInternals[Extract<keyof Database, "public">];

export type Tables<
  DefaultSchemaTableNameOrOptions extends
    | keyof (DefaultSchema["Tables"] & DefaultSchema["Views"])
    | { schema: keyof DatabaseWithoutInternals },
  TableName extends DefaultSchemaTableNameOrOptions extends (
    {
      schema: keyof DatabaseWithoutInternals;
    }
  ) ?
    keyof (DatabaseWithoutInternals[DefaultSchemaTableNameOrOptions["schema"]]["Tables"] &
      DatabaseWithoutInternals[DefaultSchemaTableNameOrOptions["schema"]]["Views"])
  : never = never,
> =
  DefaultSchemaTableNameOrOptions extends (
    {
      schema: keyof DatabaseWithoutInternals;
    }
  ) ?
    (DatabaseWithoutInternals[DefaultSchemaTableNameOrOptions["schema"]]["Tables"] &
      DatabaseWithoutInternals[DefaultSchemaTableNameOrOptions["schema"]]["Views"])[TableName] extends (
      {
        Row: infer R;
      }
    ) ?
      R
    : never
  : DefaultSchemaTableNameOrOptions extends keyof (DefaultSchema["Tables"] & DefaultSchema["Views"]) ?
    (DefaultSchema["Tables"] & DefaultSchema["Views"])[DefaultSchemaTableNameOrOptions] extends (
      {
        Row: infer R;
      }
    ) ?
      R
    : never
  : never;

export type TablesInsert<
  DefaultSchemaTableNameOrOptions extends keyof DefaultSchema["Tables"] | { schema: keyof DatabaseWithoutInternals },
  TableName extends DefaultSchemaTableNameOrOptions extends (
    {
      schema: keyof DatabaseWithoutInternals;
    }
  ) ?
    keyof DatabaseWithoutInternals[DefaultSchemaTableNameOrOptions["schema"]]["Tables"]
  : never = never,
> =
  DefaultSchemaTableNameOrOptions extends (
    {
      schema: keyof DatabaseWithoutInternals;
    }
  ) ?
    DatabaseWithoutInternals[DefaultSchemaTableNameOrOptions["schema"]]["Tables"][TableName] extends (
      {
        Insert: infer I;
      }
    ) ?
      I
    : never
  : DefaultSchemaTableNameOrOptions extends keyof DefaultSchema["Tables"] ?
    DefaultSchema["Tables"][DefaultSchemaTableNameOrOptions] extends (
      {
        Insert: infer I;
      }
    ) ?
      I
    : never
  : never;

export type TablesUpdate<
  DefaultSchemaTableNameOrOptions extends keyof DefaultSchema["Tables"] | { schema: keyof DatabaseWithoutInternals },
  TableName extends DefaultSchemaTableNameOrOptions extends (
    {
      schema: keyof DatabaseWithoutInternals;
    }
  ) ?
    keyof DatabaseWithoutInternals[DefaultSchemaTableNameOrOptions["schema"]]["Tables"]
  : never = never,
> =
  DefaultSchemaTableNameOrOptions extends (
    {
      schema: keyof DatabaseWithoutInternals;
    }
  ) ?
    DatabaseWithoutInternals[DefaultSchemaTableNameOrOptions["schema"]]["Tables"][TableName] extends (
      {
        Update: infer U;
      }
    ) ?
      U
    : never
  : DefaultSchemaTableNameOrOptions extends keyof DefaultSchema["Tables"] ?
    DefaultSchema["Tables"][DefaultSchemaTableNameOrOptions] extends (
      {
        Update: infer U;
      }
    ) ?
      U
    : never
  : never;

export type Enums<
  DefaultSchemaEnumNameOrOptions extends keyof DefaultSchema["Enums"] | { schema: keyof DatabaseWithoutInternals },
  EnumName extends DefaultSchemaEnumNameOrOptions extends (
    {
      schema: keyof DatabaseWithoutInternals;
    }
  ) ?
    keyof DatabaseWithoutInternals[DefaultSchemaEnumNameOrOptions["schema"]]["Enums"]
  : never = never,
> =
  DefaultSchemaEnumNameOrOptions extends (
    {
      schema: keyof DatabaseWithoutInternals;
    }
  ) ?
    DatabaseWithoutInternals[DefaultSchemaEnumNameOrOptions["schema"]]["Enums"][EnumName]
  : DefaultSchemaEnumNameOrOptions extends keyof DefaultSchema["Enums"] ?
    DefaultSchema["Enums"][DefaultSchemaEnumNameOrOptions]
  : never;

export type CompositeTypes<
  PublicCompositeTypeNameOrOptions extends
    | keyof DefaultSchema["CompositeTypes"]
    | { schema: keyof DatabaseWithoutInternals },
  CompositeTypeName extends PublicCompositeTypeNameOrOptions extends (
    {
      schema: keyof DatabaseWithoutInternals;
    }
  ) ?
    keyof DatabaseWithoutInternals[PublicCompositeTypeNameOrOptions["schema"]]["CompositeTypes"]
  : never = never,
> =
  PublicCompositeTypeNameOrOptions extends (
    {
      schema: keyof DatabaseWithoutInternals;
    }
  ) ?
    DatabaseWithoutInternals[PublicCompositeTypeNameOrOptions["schema"]]["CompositeTypes"][CompositeTypeName]
  : PublicCompositeTypeNameOrOptions extends keyof DefaultSchema["CompositeTypes"] ?
    DefaultSchema["CompositeTypes"][PublicCompositeTypeNameOrOptions]
  : never;

export const Constants = {
  graphql_public: {
    Enums: {},
  },
  public: {
    Enums: {
      CustomFieldDataType: ["Text", "Boolean", "List", "Number", "TextArea", "Date", "Money"],
      EstimateStatus: ["Accepted", "Pending", "Closed", "Rejected"],
      ItemType: [
        "Inventory Item",
        "Non-inventory Item",
        "Category",
        "Expense",
        "Assembly",
        "Item Group",
        "Service",
        "Labor",
        "Overhead",
        "Other",
      ],
      LongRunningGasRequestStatus: ["pending", "processing", "completed"],
      ReceivedStatus: ["", "All", "Some"],
    },
  },
} as const;
